/* eslint-disable max-lines */
import {
  DEFAULT_BRAIN,
  DEFAULT_DEBOUNCE_MAX_WAIT,
  DEFAULT_DEBOUNCE_TIME,
} from '@/components/constants';
import {
  IS_COMMS_GUARDRAILS_V2_ENABLED,
  IS_TEMPLATE_PINNING_ENABLED,
  USE_RECORDING_V2,
} from '@/features/flags';
import { getDebugLetterPayload } from '@/features/integrations-letters/letter-sender-v2/debug/letter-sender-note-panel-debugger';
import { type ImproveNotesTaskId } from '@/features/post-note-quality/improve-notes-panel/tasks.config';
import { resolveActiveSessionProvider } from '@/hooks/integrations/resolveActiveSessionProvider';
import {
  transformLocalUrlsToBase64,
  transformOnboardingStepBase64Entries,
} from '@/hooks/mutation/integration/direct-backend/onboarding-session/encoding';
import { type GenerateEhrLaunchTokenParams } from '@/hooks/mutation/integration/ehr-launch/useMutateGenerateEhrLaunchToken';
import { type GenerateEhrAuthUrlParams } from '@/hooks/mutation/integration/useMutateGenerateEhrAuthUrl';
import { GET_PINNED_TEMPLATES } from '@/hooks/query/useQueryPinnedTemplates';
import { noteGenerationEnd, noteGenerationStart } from '@/lib/analytics/scribe/general';
import {
  beginDocumentGenerateAttempt,
  documentGenerateCancelReason,
  type DocumentGenerateAttempt,
  type DocumentGenerateTrigger,
} from '@/lib/telemetry/documentGenerateTracking';
import type { FollowUpQuestion } from '@heidi/evidence/contracts';
import {
  abortArtifactGeneration,
  beginArtifactGeneration,
  completeArtifactGeneration,
  failArtifactGeneration,
  getArtifactGenerationSnapshot,
  isTranscriptDeletedInCache,
  markArtifactGenerationChunkReceived,
  markArtifactGenerationFinalizing,
  markArtifactGenerationStreamOpen,
  uploadBackupAudio as uploadBackupAudioCore,
} from '@heidi/session/data';
import type {
  ShareTemplateByEmailRequest,
  ShareTemplateByEmailResponse,
  ShareTemplateByEmailResult,
} from '@heidi/templates/contracts';
// Session keys/types read from the package like every other consumer — the
// api→session-hook upward imports (the cycle design D5 names) are cut (C4).
// The templates import above is that domain's slice and rides its own carve.
// StreamingStatus comes from the contracts door, NOT `@/models/scribeRecordModel`.
// The model re-exports the same enum, but importing it from here made this hub
// initialize the record model before the auth/store cluster — the model's own
// init then looped back through `store.ts` → `models/index.ts` into the
// still-initializing model, a TDZ crash of the SCR-4877 class (jest module
// init order; caught by scribeRecordModel.test.ts in CI when C4 removed the
// hook imports that used to warm that cluster first).
import { isEnableBlockEditorV1 } from '@/hooks/useEnableBlockEditorV1';
import {
  type ConsultNoteAICommandsResponseV2,
  type CreateChatRequest,
  type CreateChatResponse,
  type CreateEHRProfileRequest,
  type CreateUserInmailRequest,
  type DictationCleanupModesResponse,
  type DocumentAICommandsResponseV2,
  type EHRProfileResponse,
  type EmPatientType,
  type ExportAssetUrlRequest,
  type ExportAssetUrlResponse,
  type FillFormDemoFromUploadRequest,
  type FillFormDemoRequest,
  type FillFormDemoResponse,
  type FillXmlFormDemoFromUploadRequest,
  type FillXmlFormDemoRequest,
  type FillXmlFormDemoResponse,
  type GenerateTemplateContentUsingAIRequestV2,
  type GetAllFeatureFlagsResponse,
  type GetEHRLaunchAccessTokenDetailsResponse,
  type GetEHRLaunchAuthorizationURLResponse,
  type GetEHRLaunchPatientDetailsResponse,
  type GetInmailsResponse,
  type GetMobileRequest,
  type GetUserNotificationPreferencesResponse,
  type LinkPatientSessionsRequest,
  type ListRecordingsResponse,
  type MKCustomTemplateRequestResponse,
  type MKPaginatedTemplateRequestResponse,
  type RecipientSearchResponse,
  type RecordModalViewRequest,
  type SafetyIssueTicketRequest,
  type SafetyIssueTicketResponse,
  type SeenModals,
  type SendLetterResponse,
  type StoreChatsFromLocalStorageRequest,
  type StoreChatsFromLocalStorageResponse,
  type UnlinkPatientSessionsRequest,
  type UpdateAutoExtractIntentRequest,
  type UpdateAutoLinkIntentRequest,
  type UserInmailResponse,
} from '@/types';
import type {
  CreateEvidenceCheckoutRequest,
  CreateLetterFromDocumentRequest,
  DeletePersonalizationInsightRequest,
  EvidenceCheckoutResponse,
  ExportDocxRequest,
  ExportPdfRequest,
  IntegrationOnboardingStepExecutionRequest,
  IntegrationOnboardingStepExecutionResponse,
  LetterAttachmentDownloadUrlResponse,
  LetterAttachmentListResponse,
  LetterAttachmentResponse,
  LetterHeadResponse,
  LetterResponse,
  LetterTimelineEventListResponse,
  ListLettersResponse,
  PatientContextResponse,
  SignLetterRequest,
  SubmitClinicalCodeFeedbackRequest,
  TogglePersonalizationInsightRequest,
  UpdateLetterRequest,
} from '@/types/index';
import { type RepeatRequest, type RepeatStatus } from '@/types/repeats';
import type { SessionPatientDocumentLinkListResponse } from '@/types/session-patient-document-link.types';
import { getIsStarterTemplateFromCache } from '@/utils/templateCacheHelpers';
import {
  type EmLevelDeterminatorResponse,
  type SessionClinicalCodesResponse,
} from '@heidi/coding/contracts';
import { type ActivateClinicianTrialResponse } from '@heidi/commerce/contracts';
import {
  type CreatePatientProfileRequest,
  type CreatePatientProfileResponse,
  type GetPatientProfilesResponse,
  type UpdatePatientProfileRequest,
  type UpdatePatientProfileResponse,
} from '@heidi/patients/contracts';
import type { CustomStyle } from '@heidi/session/contracts';
import {
  GET_DOCUMENT_BY_ID,
  GET_SESSION_BY_ID,
  StreamingStatus,
  TRANSCRIPT_DELETED_ERROR,
  type ConfirmProblemRequest,
  type ConfirmProblemResponse,
  type QueryDocumentResponse,
  type QuerySessionResponse,
  type SessionProblemsResponse,
  type SessionResponse,
  type SessionsResponse,
  type SimpleSessionResponse,
} from '@heidi/session/contracts';
import {
  type CreatePrescriptionRequest,
  type GetClinicalTasksResponse,
  type PrescriptionResponse,
  type UpdatePrescriptionRequest,
} from '@heidi/tasks/contracts';
import type {
  FieldUpdate,
  GetFilledPdfFieldsResponse,
  UpdatePdfFieldsResponse,
} from '@heidi/templates/contracts';
import {
  type GetTemplatesResponse,
  type TemplateResponse,
} from '@heidi/templates/contracts';
import type { PersonalizationDataResponse } from '@heidi/workspace/contracts';

import { documentsGeneratedInSessionAtom } from '@/atoms/sessionAtoms';
import type {
  CreateSessionLinkShareRequest,
  CreateSessionLinkShareResponse,
  GetSessionLinkSharesResponse,
  RevokeSessionLinkShareResponse,
} from '@/components/modal/share-session-modal/share-session-modal.types';
import { getDomains } from '@/helpers/getDomains';

import { getUrlFromConfig } from '@/utils/endpoints';
import { customFetchEventSource } from '@/utils/fetchEventSource';
import { getAuthTokenForHttpClientSync } from '@/utils/http/getAuthTokenForHttpClientSync';
import { logger } from '@/utils/logger/logger';
import { reactQueryClient } from '@/utils/reactQueryClient';
import { removeUnnecessaryCharacters } from '@/utils/removeUnnecessaryCharacters';
import { scribeHttpV2, scribeHttpV2_1 } from '@/utils/scribeHTTP';
import { getTempUserId } from '@/utils/tempUser';
import { type EventSourceMessage } from '@fortaine/fetch-event-source';
import {
  ContentType,
  type Brain,
  type DocumentGenType,
  type EHRProvider,
  type Region,
  type VoiceStyle,
  type components,
} from '@heidi/schema/ml-scribe';
import axios, { type AxiosRequestConfig, type AxiosResponse } from 'axios';
import { getDefaultStore } from 'jotai';
import { debounce } from 'lodash-es';
import { updateDocument } from './scribeAPI';

const jotaiStore = getDefaultStore();

/** @domain REVIEW */
export const buildDocumentContentPath = (sessionId: string, documentId: string) => {
  return `sessions/${sessionId}/documents/${documentId}/content`;
};

/** @domain REVIEW */
export const buildDocumentContentRequestPath = (
  sessionId: string,
  documentId: string
) => {
  return `api/v2-1/ml-scribe/${buildDocumentContentPath(sessionId, documentId)}`;
};

/** @domain session */
export const askAI = ({
  sessionId,
  content,
  aiCommandText,
}: {
  sessionId: string;
  content: string;
  aiCommandText: string;
}) => {
  return scribeHttpV2.post<string>(`/sessions/${sessionId}/ask-ai`, {
    content,
    ai_command_text: aiCommandText,
  });
};

// Carved to `@heidi/patients/data` at ARCFE-1011 — all 20 `@domain patients`
// endpoints plus their shared `patientSummarySourcePath` helper. Name-intact
// re-exports so every existing importer and `jest.mock` literal keeps
// resolving; Z1 codemods them to the door and Z2 deletes this block.
//
// Seven of these were blocked until C2b gave their request/response types a
// package home. Copying the types would have produced two definitions of one
// wire contract — the same drift hazard as a duplicated cache key.
export {
  createPatientProfile,
  deletePatientDocument,
  deletePatientProfile,
  deletePatientProfiles,
  getPatientDocumentWithDownloadUrl,
  getPatientDocuments,
  getPatientProfiles,
  getPatientRecap,
  getPatientSummaryEmailPreview,
  getPatientSummaryPreviewPdf,
  importPatientProfiles,
  linkSessionsToPatientProfile,
  mergePatientProfiles,
  preparePatientSummaryDelivery,
  searchPatientProfiles,
  sendPatientSummaryDelivery,
  sendPatientSummaryEmailDelivery,
  unlinkSessionsToPatientProfile,
  updatePatientProfile,
  uploadPatientDocument,
} from '@heidi/patients/data';

/**
 * Base path for a send source — a saved document, or a session's consult note.
 * The backend mounts the note routes as siblings of the document ones.
 */

/**
 * Helper function to get isPinnedTemplate from cache.
 * Returns false when pinning is disabled or templateId is falsy,
 * otherwise reads from reactQueryClient and checks if template is in user's pinned templates.
 * NOTE: This only checks user-pinned templates, NOT starter pack templates.
 */
const getIsPinnedTemplateFromCache = (templateId?: string | null): boolean => {
  if (!IS_TEMPLATE_PINNING_ENABLED() || !templateId) {
    return false;
  }
  const pinnedTemplates = reactQueryClient.getQueryData<TemplateResponse[]>([
    GET_PINNED_TEMPLATES,
  ]);
  return pinnedTemplates?.some((t) => t.id === templateId) ?? false;
};

type GenerateDocumentParams = {
  sessionId: string;
  documentId: string;
  /** Only `explicit` emits intent. */
  trigger: DocumentGenerateTrigger;

  templateId?: string | null;
  aiCommandId?: string | null;
  aiCommandText?: string | null;
  generationMethod?: DocumentGenType | null;
  brain?: Brain;
  voiceStyle?: VoiceStyle;
  customStyle?: CustomStyle;
  onAbort?: (documentBeforeAbort: QueryDocumentResponse | undefined) => void;
  isPersonalizationApplied?: boolean;
  isNotRegeneration?: boolean;
};

const streamDocument = (
  {
    sessionId,
    documentId,

    templateId,
    aiCommandId,
    aiCommandText,
    generationMethod,
    brain,
    voiceStyle,
    customStyle,
    onAbort,
    isPersonalizationApplied,
    isNotRegeneration,
  }: GenerateDocumentParams,
  attempt: DocumentGenerateAttempt
) => {
  // Belt: block once the transcript is deleted; hooks map the sentinel to the
  // block toast (same shape as `SessionNotTranscribedError`).
  if (isTranscriptDeletedInCache(sessionId)) {
    const error = new Error(TRANSCRIPT_DELETED_ERROR);
    attempt.fail(error);
    return Promise.reject(error);
  }
  const documentFromCache = reactQueryClient.getQueryData<QueryDocumentResponse>([
    GET_DOCUMENT_BY_ID,
    { sessionId, documentId },
  ]);
  const session = reactQueryClient.getQueryData<QuerySessionResponse>([
    GET_SESSION_BY_ID,
    { sessionId: sessionId },
  ]);
  const previousDocumentContent = documentFromCache?.content;
  const documentGenerationKey = { kind: 'document', sessionId, documentId } as const;
  const { generationId, controller: abortController } = beginArtifactGeneration({
    key: documentGenerationKey,
    outgoingContent: previousDocumentContent ?? null,
  });
  const isCurrentDocumentGeneration = () =>
    getArtifactGenerationSnapshot(documentGenerationKey).context.generationId ===
    generationId;

  // Get isPinnedTemplate and isStarterPackTemplate once for tracking throughout
  const isPinnedTemplate = getIsPinnedTemplateFromCache(templateId);
  const isStarterPackTemplate = getIsStarterTemplateFromCache(templateId);

  // Discard any in-flight GET for this document first. Its response would land
  // mid-stream and re-seed `streamingStatus` to NOT_STARTED with a fresh
  // `AbortController`, after which the chunk writer below bails on every
  // remaining chunk and Stop aborts a controller nobody is listening to.
  void reactQueryClient.cancelQueries({
    queryKey: [GET_DOCUMENT_BY_ID, { sessionId, documentId }],
  });

  // set streaming status to AWAITING_STREAMING_START
  reactQueryClient.setQueryData<QueryDocumentResponse>(
    [GET_DOCUMENT_BY_ID, { sessionId, documentId }],
    (oldData) => {
      const newData: QueryDocumentResponse = {
        ...oldData,
        document_tab_type: oldData?.document_tab_type ?? null,
        brain: brain || oldData?.brain || DEFAULT_BRAIN,
        voice_style: voiceStyle || oldData?.voice_style,
        content: '',
        id: documentId,
        session_id: sessionId,
        streamingStatus: StreamingStatus.AWAITING_STREAMING_START,
        abortController,
      };
      // track note generation start
      noteGenerationStart({
        sessionId,
        documentId,
        startTime: performance.now() + performance.timeOrigin,
        isRecordingV2Enabled: USE_RECORDING_V2(),
        isTabSharing: session?.is_tab_sharing ?? false,
        is_pinned_template: isPinnedTemplate,
        is_starter_pack_template: isStarterPackTemplate,
      });
      return newData;
    }
  );

  const debounceSetDocumentQueryData = debounce(
    (newText: string) => {
      if (!isCurrentDocumentGeneration()) return;
      reactQueryClient.setQueryData<QueryDocumentResponse>(
        [GET_DOCUMENT_BY_ID, { sessionId, documentId }],
        (oldData) => {
          if (!oldData) {
            return oldData;
          }

          if (
            oldData.streamingStatus !== StreamingStatus.STREAMING &&
            oldData.streamingStatus !== StreamingStatus.AWAITING_STREAMING_START &&
            oldData.streamingStatus !== StreamingStatus.FINISHING_STREAMING
          ) {
            return oldData;
          }

          const newData: QueryDocumentResponse = {
            ...oldData,
            content: newText,
            streamingStatus: StreamingStatus.STREAMING,
          };
          return newData;
        }
      );
    },
    DEFAULT_DEBOUNCE_TIME,
    { leading: true, maxWait: DEFAULT_DEBOUNCE_MAX_WAIT }
  );
  // A rejected stream leaves the cache mid-generation; show it as failed and
  // put back the content it started from, dropping any partial output.
  const markDocumentGenerationFailed = () =>
    reactQueryClient.setQueryData<QueryDocumentResponse>(
      [GET_DOCUMENT_BY_ID, { sessionId, documentId }],
      (oldData) =>
        oldData && {
          ...oldData,
          content: previousDocumentContent ?? '',
          streamingStatus: StreamingStatus.GENERATION_FAILED,
          abortController: new AbortController(),
        }
    );
  let accumulatedText = '';
  let responseStatus: number | undefined;
  const enableBlockEditorV1 = isEnableBlockEditorV1(
    resolveActiveSessionProvider(sessionId)
  );
  const documentContentPath = buildDocumentContentPath(sessionId, documentId);

  return Promise.resolve(
    customFetchEventSource({
      endPointVersion: 'v2-1',
      path: documentContentPath,
      requestBody: generationMethod && {
        template_id: templateId,
        ai_command_id: aiCommandId,
        ai_command_text: aiCommandText,
        generation_method: generationMethod,
        brain: brain,
        voice_style: voiceStyle,
        custom_style: customStyle,
        content_type: enableBlockEditorV1 ? ContentType.HTML : ContentType.MARKDOWN,
        is_personalization_applied: isPersonalizationApplied,
        is_not_regeneration: isNotRegeneration,
      },
      abortController,
      onAbort: () => {
        if (
          abortController.signal.reason === 'superseded' ||
          !isCurrentDocumentGeneration()
        ) {
          return;
        }
        abortArtifactGeneration(documentGenerationKey);
        if (onAbort) {
          onAbort(documentFromCache);
          return;
        }
        reactQueryClient.setQueryData<QueryDocumentResponse>(
          [GET_DOCUMENT_BY_ID, { sessionId, documentId }],
          (oldData) => {
            if (!oldData) {
              return undefined;
            }

            return {
              ...oldData,
              content: oldData?.content || previousDocumentContent,
              streamingStatus: StreamingStatus.MANUALLY_ABORTED,
              abortController: new AbortController(),
            };
          }
        );
      },
      onmessage: (ev) => {
        if (!isCurrentDocumentGeneration()) return;
        const data = ev.data;
        let transformedChar = data === '' ? '\n' : data.replace(/ {3}/g, '\n');
        if (enableBlockEditorV1) {
          transformedChar = removeUnnecessaryCharacters(transformedChar);
        }
        accumulatedText += transformedChar;
        if (enableBlockEditorV1) {
          accumulatedText = removeUnnecessaryCharacters(accumulatedText);
        }
        debounceSetDocumentQueryData(accumulatedText);
        markArtifactGenerationChunkReceived(documentGenerationKey);
      },
      onopen: (status) => {
        responseStatus = status;
        if (!isCurrentDocumentGeneration()) return;
        markArtifactGenerationStreamOpen(documentGenerationKey);
        reactQueryClient.setQueryData<QueryDocumentResponse>(
          [GET_DOCUMENT_BY_ID, { sessionId, documentId }],
          (oldData) => {
            if (!oldData) {
              return undefined;
            }

            const newData: QueryDocumentResponse = {
              ...oldData,
              streamingStatus: StreamingStatus.STREAMING,
            };
            return newData;
          }
        );
      },
      onclose: () => {
        if (!isCurrentDocumentGeneration()) {
          attempt.cancel('superseded');
          return;
        }
        debounceSetDocumentQueryData.flush();
        markArtifactGenerationFinalizing(documentGenerationKey);
        const onClose = async () => {
          if (!isCurrentDocumentGeneration()) {
            attempt.cancel('superseded');
            return;
          }
          try {
            reactQueryClient.setQueryData<QueryDocumentResponse>(
              [GET_DOCUMENT_BY_ID, { sessionId, documentId }],
              (oldData) => {
                if (!oldData) {
                  return undefined;
                }

                const newData: QueryDocumentResponse = {
                  ...oldData,
                  streamingStatus: StreamingStatus.FINISHING_STREAMING,
                  abortController: new AbortController(),
                };
                return newData;
              }
            );
            if (enableBlockEditorV1) {
              await updateDocument({
                sessionId,
                documentId,
                content: accumulatedText,
                contentType: ContentType.HTML,
              });
            }
            if (!isCurrentDocumentGeneration()) {
              attempt.cancel('superseded');
              return;
            }

            // Mark document as generated in this session for pinning tooltip
            jotaiStore.set(documentsGeneratedInSessionAtom, (prev: Set<string>) => {
              const updated = new Set(prev);
              updated.add(documentId);
              logger.debug(
                '[generateDocument] Added document to documentsGeneratedInSession',
                {
                  documentId,
                  sessionId,
                  previousSize: prev.size,
                  newSize: updated.size,
                  allDocuments: Array.from(updated),
                }
              );
              return updated;
            });

            // track note generation end
            noteGenerationEnd({
              sessionId,
              documentId,
              endTime: performance.now() + performance.timeOrigin,
              is_pinned_template: isPinnedTemplate,
              is_starter_pack_template: isStarterPackTemplate,
            });
            // Non-2xx rejects before this. An empty 2xx still completes (a
            // product decision); only telemetry counts it as failed.
            if (accumulatedText.trim() === '') {
              logger.warn('[generateDocument] document stream closed empty', {
                status: responseStatus,
              });
              attempt.failEmptyStream();
            } else {
              attempt.complete();
            }
          } catch (error) {
            attempt.fail(error);
            if (isCurrentDocumentGeneration()) {
              failArtifactGeneration(documentGenerationKey, error);
            }
          } finally {
            if (isCurrentDocumentGeneration()) {
              completeArtifactGeneration(documentGenerationKey);
            }
          }
        };
        setTimeout(() => {
          void onClose();
        }, 1);
      },
    })
  ).catch((error: unknown) => {
    if (error instanceof Error && error.message === 'StreamManuallyAbortedError') {
      attempt.cancel(documentGenerateCancelReason(abortController.signal.reason));
    } else {
      attempt.fail(error);
      // A 400/403 has its own surface (quota, access, not transcribed), so it
      // keeps its existing handling.
      const status = (error as { status?: unknown } | null)?.status;
      if (isCurrentDocumentGeneration() && status !== 400 && status !== 403) {
        markDocumentGenerationFailed();
      }
    }
    if (isCurrentDocumentGeneration()) {
      failArtifactGeneration(documentGenerationKey, error);
    }
    throw error;
  });
};

/** @domain REVIEW */
export const generateDocument = (params: GenerateDocumentParams) => {
  const attempt = beginDocumentGenerateAttempt({
    trigger: params.trigger,
    generationMethod: params.generationMethod,
  });
  try {
    return streamDocument(params, attempt);
  } catch (error) {
    // Close the attempt on a synchronous throw too.
    attempt.fail(error);
    throw error;
  }
};

// Carved to `@heidi/templates/data` at ARCFE-1012 — all 34 `@domain templates`
// endpoints, name-intact re-exports so every existing importer and `jest.mock`
// literal keeps resolving; Z1 codemods them to the door and Z2 deletes this
// block.
//
// FOUR SESSION ENDPOINTS STAYED, deliberately: `generateDocument`,
// `updateConsultNote`, `getSessionTranscript` and `getDictationCleanupModes`
// live in this neighbourhood because template flows call them, but they are
// Session's — `generateDocument` is the symbol Session's E3 ruled stays
// app-side. The templates family selector excludes all four by name.
export {
  createLatestVersionControl,
  createPublicTemplateSuggestions,
  createSharedTemplate,
  createTemplate,
  createTemplateCreationRecord,
  createTemplateCreationRecordByTemplateId,
  createTemplateSuggestions,
  deleteTemplateV2,
  deleteTemplateVersionControl,
  editTemplate,
  editTemplateFavoriteStatus,
  editTemplatePinStatus,
  editTemplatePublic,
  generateExampleNotePublic,
  generateTemplatePublic,
  getConsultNoteTemplates,
  getDocumentTemplates,
  getInitialPinnedTemplates,
  getInternalTemplateById,
  getOrganizationTemplates,
  getOrganizationTemplatesMetadata,
  getPinnedTemplates,
  getRecommendedTemplates,
  getTemplateById,
  getTemplateCreationRecordById,
  getTemplateCreationRecordByTemplateId,
  getTemplates,
  processUploadedDocument,
  processUploadedDocumentPublic,
  setInitialPinnedTemplates,
  shareTemplateByEmail,
  updatePinnedTemplateOrder,
  updateTemplateCreationRecord,
  updateTemplateVersionControl,
} from '@heidi/templates/data';

// get consult note templates by Id
/** @domain session */
export const getConsultNoteTemplatesById = (sessionId: string) => {
  return scribeHttpV2.get<GetTemplatesResponse>(
    `/sessions/${sessionId}/consult-note/templates`
  );
};

// get consult note templates

// get document templates

// generate_template_content_using_ai_api_v2_ml_scribe_templates_ai_gen_content_post
/** @domain REVIEW */
export const generateTemplateContentUsingAICommand = ({
  aiCommandText,
  onOpen,
  onMessage,
}: {
  aiCommandText: string;
  onOpen: () => void;
  onMessage: (ev: EventSourceMessage) => void;
}) => {
  const enableBlockEditorV1 = isEnableBlockEditorV1();
  return customFetchEventSource({
    path: `templates/ai-gen-content`,
    endPointVersion: 'v2-1',
    requestBody: {
      ai_command_text: aiCommandText,
      content_type: enableBlockEditorV1 ? ContentType.HTML : ContentType.MARKDOWN,
    } satisfies GenerateTemplateContentUsingAIRequestV2,
    onopen: onOpen,
    onmessage: onMessage,
    abortController: new AbortController(),
  });
};

/** @domain REVIEW */
export const getAICommandsBySessionId = (sessionId: string) => {
  return scribeHttpV2_1.get<ConsultNoteAICommandsResponseV2>(
    `/sessions/${sessionId}/consult-note/ai-commands`
  );
};

/** @domain REVIEW */
export const getDocumentAICommandsBySessionId = (sessionId: string) => {
  return scribeHttpV2_1.get<DocumentAICommandsResponseV2>(
    `/sessions/${sessionId}/documents/ai-commands`
  );
};

// Carved to `@heidi/session/data` at ARCFE-1004 — the document + context-document
// writes, name-intact re-exports so every existing importer and `jest.mock`
// literal keeps resolving. NOTE: `postCreateEmptyDocument` now takes a
// resolved `contentType` (the block-editor flag composed with capture's
// provider resolution is an APP read — the E3 flag disposition), so its call
// sites pass it in.
export {
  deleteContextDocuments,
  getContextDocumentFile,
  postCreateEmptyDocument,
  shareDocumentByEmail,
  updateContextDocuments,
  updateDocuments,
  uploadContextDocument,
  type ShareDocumentByEmailRecipientsRequest,
  type UploadContextDocumentsMetaData,
} from '@heidi/session/data';

// Carved to `@heidi/session/data` with the D4 family — the `@domain session`
// tag retires with the carve.
export { getConsultNotes } from '@heidi/session/data';

// get the unmerged merge candidates for a session (GET /merged-sessions)
/** @domain session */
// Carved to `@heidi/session/data` with the D6 family — the `@domain session`
// tag retires with the carve (inside the package, the whole file is session's).
export { getMergeableSessions } from '@heidi/session/data';

// Carved to `@heidi/session/data` with the D4 family — the `@domain session`
// tag retires with the carve.
export { getSessionConsultNoteTimeline } from '@heidi/session/data';

// Carved to `@heidi/session/data` with the D4 family — the `@domain session`
// tag retires with the carve.
export { revertSessionConsultNote } from '@heidi/session/data';

// `generateNoteUsingAICommand` (`POST /sessions/{id}/consult-note`) was
// DELETED at ARCFE-1096, not carved. Measured at HEAD it had exactly one
// caller — `src/hooks/mutation/useMutateGenerateNoteUsingAICommand.ts`, itself
// referenced by nothing but its own test (the Ask-AI note command was retired
// from the UI long ago; the hook still carried a commented-out
// create-template dialog from that era). Precedent for a zero-consumer hub
// endpoint is DELETE RATHER THAN CARVE (`deleteAllSessionClinicalTasks`,
// re-applied to five calls endpoints at ARCFE-1038), because carving dead
// code moves the debt into the package instead of removing it. The
// transcript-deleted belt that guarded it goes with it; `generateDocument`
// below keeps its own, and `scribeAPIV2.transcriptDeleted.test.ts` still
// pins that one.

/**
 * Send a document (e.g. PDF tab) to one or more email addresses.
 *
 * Mirrors {@link shareConsultNoteByEmail} — multi-recipient send with
 * `sent_recipients` / `failed_recipients` in the response for partial-failure UX.
 */

// The two request/response types (and the result element) moved to
// `@heidi/templates/contracts` with the endpoints (D1, ARCFE-1012);
// re-exported here for existing importers and `jest.mock` literals until Z1.
// Kept ABOVE the `@domain` tag, not between it and its endpoint — the
// domain-tags suite pairs a tag with the export that follows it, and inserting
// anything in between silently un-tags the endpoint.
export type {
  ShareTemplateByEmailRequest,
  ShareTemplateByEmailResponse,
  ShareTemplateByEmailResult,
};

// Carved to `@heidi/templates/data` at ARCFE-1015 with a recorded DOMAIN
// RULING (the fn was `@domain REVIEW`; the ruling lives in the package module,
// per src/api/CLAUDE.md). It keeps `openApiHttp` — the shared-template page is
// public by design.
export { getSharedTemplate } from '@heidi/templates/data';

// Carved to `@heidi/session/data` with the D2 transcript family (the @domain
// tags travel with the fns). Re-exported so the `@/pages/api/scribeAPIV2`
// chain and its jest.mock literals keep resolving; deleted at Z2.
export { generateBackupTranscript, getSessionTranscript } from '@heidi/session/data';

/** @domain workspace */
export const getUserEHR = () => {
  return scribeHttpV2.get<EHRProfileResponse>('/user/ehr-profile');
};

// Carved to `@heidi/integrations/ehr` at task 3.11 D1 (ARCFE-1087) — the 43
// `@domain integrations` endpoints this door owns by route. The `@domain`
// tags travel with the fns; `getHaikuLaunchInfo` keeps its REVIEW tag, which
// is why its family arm claims it by symbol under a `domainTagWaiver`.
// Name-intact re-export so the `@/pages/api/scribeAPIV2` compat chain and the
// jest.mock literals across `src/hooks/{query,mutation}/integration**` keep
// resolving; deleted at Z2.
export {
  checkMindbodySiteAccess,
  pairBPSite,
  validatePairingCode,
} from '@heidi/integrations/ehr';

// Carved to `@heidi/integrations/letters` at ARCFE-1087 — the door's ENTIRE
// endpoint claim, both `/ehr/letter/**` routes. The 13
// `/sessions/:id/letters**` endpoints below are integrations-owned records
// rather than door surface, and deliberately stay here; `postOutgoingLetter`
// keeps its `@domain REVIEW` tag, since the family claims it by route.
export { getEhrLetterTemplates, postOutgoingLetter } from '@heidi/integrations/letters';

/** @domain REVIEW */
export const createLetterFromDocument = (
  sessionId: string,
  body: CreateLetterFromDocumentRequest
) => {
  return scribeHttpV2.post<LetterResponse>(`/sessions/${sessionId}/letters`, body);
};

/** @domain REVIEW */
export const getLetters = (sessionId: string) => {
  const debugLetterPayload = getDebugLetterPayload(sessionId);

  if (debugLetterPayload) {
    return Promise.resolve(debugLetterPayload);
  }

  return scribeHttpV2.get<ListLettersResponse>(`/sessions/${sessionId}/letters`);
};

/** @domain REVIEW */
export const deleteLetter = (sessionId: string, letterId: string) => {
  return scribeHttpV2.delete(`/sessions/${sessionId}/letters/${letterId}`);
};

/** @domain REVIEW */
export const getLetterHead = (sessionId: string, letterId: string) => {
  return scribeHttpV2.get<LetterHeadResponse>(
    `/sessions/${sessionId}/letters/${letterId}/letterhead`
  );
};

/** @domain REVIEW */
export const getLetterAttachmentsV2 = (sessionId: string, letterId: string) => {
  return scribeHttpV2.get<LetterAttachmentListResponse>(
    `/sessions/${sessionId}/letters/${letterId}/attachments`
  );
};

/** @domain REVIEW */
export const getLetterTimelineEvents = (sessionId: string, letterId: string) => {
  return scribeHttpV2.get<LetterTimelineEventListResponse>(
    `/sessions/${sessionId}/letters/${letterId}/timeline-events`
  );
};

/** @domain REVIEW */
export const getLetterAttachmentDownloadUrlV2 = (
  sessionId: string,
  letterId: string,
  attachmentId: string
) => {
  return scribeHttpV2.get<LetterAttachmentDownloadUrlResponse>(
    `/sessions/${sessionId}/letters/${letterId}/attachments/${attachmentId}/download-url`
  );
};

/** @domain REVIEW */
export const deleteLetterAttachmentV2 = (
  sessionId: string,
  letterId: string,
  attachmentId: string
) => {
  return scribeHttpV2.delete(
    `/sessions/${sessionId}/letters/${letterId}/attachments/${attachmentId}`
  );
};

/** @domain REVIEW */
export const uploadLetterAttachmentV2 = ({
  sessionId,
  letterId,
  file,
  onProgressUpdate,
  signal,
}: {
  sessionId: string;
  letterId: string;
  file: File;
  onProgressUpdate?: (progress: number) => void;
  signal?: AbortSignal;
}) => {
  const formData = new FormData();
  formData.append('file', file);

  return scribeHttpV2.post<LetterAttachmentResponse>(
    `/sessions/${sessionId}/letters/${letterId}/attachments`,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      signal,
      onUploadProgress: (progressEvent) => {
        if (progressEvent.total && onProgressUpdate) {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          onProgressUpdate(percentCompleted);
        }
      },
    }
  );
};

/** @domain REVIEW */
export const sendLetter = ({
  sessionId,
  letterId,
  pdfFile,
}: {
  sessionId: string;
  letterId: string;
  pdfFile: File;
}) => {
  const formData = new FormData();
  formData.append('file', pdfFile);

  return scribeHttpV2.put<SendLetterResponse>(
    `/sessions/${sessionId}/letters/${letterId}:send`,
    formData
  );
};

/** @domain REVIEW */
export const signLetter = (
  sessionId: string,
  letterId: string,
  body: SignLetterRequest
) => {
  return scribeHttpV2.put<LetterResponse>(
    `/sessions/${sessionId}/letters/${letterId}:sign`,
    body
  );
};

/** @domain REVIEW */
export const updateLetter = (
  sessionId: string,
  letterId: string,
  body: UpdateLetterRequest
) => {
  return scribeHttpV2.patch<LetterResponse>(
    `/sessions/${sessionId}/letters/${letterId}`,
    body
  );
};

// More of the ARCFE-1087 `@heidi/integrations/ehr` carve — see the
// first block above for the rationale.
export {
  getEHRUsers,
  getEHRUsersByClinicId,
  getEHRUsersV2,
} from '@heidi/integrations/ehr';

/** @domain workspace */
export const connectIntegration = (payload: CreateEHRProfileRequest) => {
  return scribeHttpV2.post<EHRProfileResponse>('/user/ehr-profile', payload);
};

// More of the ARCFE-1087 `@heidi/integrations/ehr` carve — see the
// first block above for the rationale.
export {
  connectIntegrationEhrLaunch,
  deleteIntegrationEhrLaunch,
  getEHRAppointments,
  getIntegrationsSchedule,
  getUserEHRLaunchProfile,
  getUserEHRLaunchProfileAuth0,
  updateIntegrationEhrLaunch,
} from '@heidi/integrations/ehr';
export type { GetIntegrationsScheduleRequestParams } from '@heidi/integrations/ehr';

/** @domain REVIEW */
export const searchLetterRecipients = (sessionId: string, params: { name: string }) => {
  return scribeHttpV2.get<RecipientSearchResponse>(
    `/sessions/${sessionId}/letters:search-recipients`,
    {
      params,
    }
  );
};

export interface SharepointSyncFolderConfig {
  folder_id: string;
  name: string | null;
  web_url: string | null;
  is_enabled: boolean;
  document_count: number;
  last_synced_at: string | null;
}

export interface SharepointSyncSiteConfig {
  site_id: string;
  name: string | null;
  web_url: string | null;
  is_enabled: boolean;
  document_count: number;
  last_synced_at: string | null;
  folders: SharepointSyncFolderConfig[];
}

export interface SharepointSyncConfigResponse {
  connected_microsoft_email: string | null;
  first_connected_at: string | null;
  source_link: string | null;
  sites: SharepointSyncSiteConfig[];
}

export interface SharepointSyncFolderSelection {
  folder_id: string;
  is_enabled: boolean;
}

export interface SharepointSyncSiteSelection {
  site_id: string;
  is_enabled: boolean;
  folders: SharepointSyncFolderSelection[];
}

export interface SharepointSyncConfigUpdateRequest {
  sites: SharepointSyncSiteSelection[];
}

export interface SharepointSiteInfo {
  site_id: string;
  name: string | null;
  web_url: string | null;
}

export interface SharepointSitesResponse {
  sites: SharepointSiteInfo[];
}

export interface SharepointSiteContentItem {
  item_id: string;
  name: string | null;
  web_url: string | null;
  size: number | null;
  is_folder: boolean;
}

export interface SharepointSiteContentsResponse {
  items: SharepointSiteContentItem[];
}

export interface SharePointOAuthStartResponse {
  redirect_url: string;
}

/** @domain evidence */
export const getSharepointOauthStartRedirectUrl = () => {
  return scribeHttpV2.get<SharePointOAuthStartResponse>(
    '/evidence/sharepoint/oauth/start'
  );
};

/** @domain evidence */
export const getSharepointConfig = () => {
  return scribeHttpV2.get<SharepointSyncConfigResponse>('/evidence/sharepoint/config');
};

/** @domain evidence */
export const updateSharepointConfig = (payload: SharepointSyncConfigUpdateRequest) => {
  return scribeHttpV2.patch<SharepointSyncConfigResponse>(
    '/evidence/sharepoint/config',
    payload
  );
};

/** @domain evidence */
export const resyncSharepointConfig = () => {
  return scribeHttpV2.post<SharepointSyncConfigResponse>(
    '/evidence/sharepoint/config:resync'
  );
};

/** @domain evidence */
export const deleteSharepointConfig = () => {
  return scribeHttpV2.delete('/evidence/sharepoint/config');
};

/** @domain evidence */
export const getSharepointSites = () => {
  return scribeHttpV2.get<SharepointSitesResponse>('/evidence/sharepoint/sites');
};

/** @domain evidence */
export const getSharepointSiteContents = (siteId: string) => {
  return scribeHttpV2.get<SharepointSiteContentsResponse>(
    '/evidence/sharepoint/site-contents',
    {
      params: { site_id: siteId },
    }
  );
};

/** @domain evidence */
export const getSharepointSiteFolderContents = (siteId: string, folderPath: string) => {
  return scribeHttpV2.get<SharepointSiteContentsResponse>(
    '/evidence/sharepoint/site-folder-contents',
    {
      params: { site_id: siteId, folder_path: folderPath },
    }
  );
};

// More of the ARCFE-1087 `@heidi/integrations/ehr` carve — see the
// first block above for the rationale.
export {
  syncNotesToEHR,
  syncNotesToEHRWithEHRLaunch,
  syncSDEsToEHR,
  syncSectionsNoteToEHR,
} from '@heidi/integrations/ehr';

/** @domain workspace */
export const deleteIntegration = () => {
  return scribeHttpV2.delete(`/user/ehr-profile`);
};

/** @domain growth */
export const generateReferralLink = (paypalEmail: string) => {
  return scribeHttpV2.post<string>('/referrals/referral-links', {
    paypal_email: paypalEmail,
  });
};

// Ruling 22 (ARCFE-1127): the ONE implementation of
// `POST /sessions/{id}/backup-audios/` now lives in
// `packages/session/src/data/api/backupAudio.ts`, shared with the capture
// provider's `uploadBackup` adapter. A bodied DELEGATE rather than a
// name-intact re-export, because both things this path adds on top of the
// transport are app concerns the headless package must not carry: the
// `logger.debug` on every progress event, and the percent rounding its
// `uploadProgressCallback` contract promises.
/** @domain session */
export const uploadBackupAudio = ({
  sessionId,
  file,
  fileExtension,
  startTime,
  endTime,
  uploadProgressCallback,
}: {
  sessionId: string;
  file: Blob;
  fileExtension: string | undefined;
  startTime: number | null;
  endTime: number | null;
  uploadProgressCallback?: (progress: number) => void;
}) => {
  return uploadBackupAudioCore({
    sessionId,
    file,
    extension: fileExtension,
    startTime,
    endTime,
    onProgressEvent: (progressEvent) => {
      logger.debug(progressEvent);
      if (progressEvent.total) {
        const percentCompleted = Math.round(
          (progressEvent.loaded * 100) / progressEvent.total
        );
        uploadProgressCallback?.(percentCompleted);
      }
    },
  });
};

// update once backend finish
// export const editTemplate = (template: UpdateTemplateRequest) => {

// Carved to `@heidi/workspace/data` at EN3 (ARCFE-997) — the 8
// `access-entitlements/*` team-admin endpoints, name-intact re-exports so
// every existing importer and `jest.mock` literal keeps resolving. The frozen
// oddities (the GRO-3640 create-or-update PUTs, the DELETE-with-body shape)
// moved verbatim; see the package module.
export {
  assignTeamEntitlementAsTeamAdmin,
  assignUserEntitlementAsTeamAdmin,
  bulkAssignUserEntitlementsAsTeamAdmin,
  bulkDeleteUserEntitlementsAsTeamAdmin,
  deleteTeamEntitlementAsTeamAdmin,
  deleteUserEntitlementAsTeamAdmin,
  getTeamAccessEntitlements,
  getTeamMemberAccessEntitlements,
} from '@heidi/workspace/data';

// Carved to `@heidi/session/data` with the session-header modal cores —
// a sessions read whose own-sessions twin below is
// `@domain session` and whose by-id neighbour carved at D3; the `@domain
// workspace` tag retires with the carve. Re-exported so existing importers +
// `jest.mock` literals keep resolving until Z1's codemod.
export { getTeamMemberSessionsByPatientProfileId } from '@heidi/session/data';

// Moved to `@heidi/session/data` with the D3 family (the `@domain session`
// tag retires with the carve); re-exported so existing importers +
// `jest.mock` literals keep resolving until Z1's codemod.
export { getTeamMemberSessionById } from '@heidi/session/data';

/** @domain workspace */
export const postCreateSessionForOtherUser = ({
  teamId,
  memberId,
  timezone,
  signal,
}: {
  teamId: string;
  memberId: string;
  timezone?: string;
  signal?: AbortSignal;
}) => {
  return scribeHttpV2.post<SessionResponse>(
    `teams/${teamId}/members/${memberId}/sessions`,
    {
      timezone,
    },
    { signal }
  );
};

// Moved to `@heidi/session/data` with the D3 family (tag retires with the
// carve); re-exported for existing importers + `jest.mock` literals.
export { postBulkCreateSessionsForTeamMember } from '@heidi/session/data';

// Carved to `@heidi/session/data` with the session-header modal cores;
// the `@domain session` tag retires with the carve. Re-exported for
// existing importers + `jest.mock` literals.
export { updateSessionForOtherUser } from '@heidi/session/data';

// Carved to `@heidi/workspace/data` at D1 (ARCFE-1037) — the three V2
// user-record write endpoints (with the five `updateUser*` writes leaving
// `scribeAPI.ts` in the same carve), name-intact re-exports so every existing
// importer and `jest.mock` literal keeps resolving. Their `@domain REVIEW`
// tags retire with the carve (ruling: workspace/identity-projection — each
// writes the user's own record). Transport is unchanged: the package fns
// resolve this module's own `scribeHttpV2` singleton through the
// `@heidi/api/client` registry. A suite that mocks `@/utils/scribeHTTP` no
// longer intercepts these — it must ALSO set a registry double
// (`setScribeHttpClients`).
//
// ARCFE-1141 added four more: the MUL-1879 user session-timeout pair
// (`setUserSessionTimeout`/`clearUserSessionTimeout` — the identity family's own
// task-1.4 exclusion re-read, because this package already holds the ORG half of
// the same preference pair), `getOrgAnalyticsDashboard` (the ARCFE-1060
// downgrade's named heal path) and `getJoinableTeams`. Same clients, same routes
// and verbs, name-intact.
export {
  clearUserSessionTimeout,
  createUser,
  getJoinableTeams,
  getOrgAnalyticsDashboard,
  setUserSessionTimeout,
  toggleUserMfa,
  userSelfOnboard,
} from '@heidi/workspace/data';

/** @domain workspace */
export const toggleTeamPatientRetention = (input: {
  patientRetentionEnabled: boolean;
  retentionDays?: number;
  teamId: string;
}) => {
  return scribeHttpV2.patch(`/teams/${input.teamId}/patient-retention`, {
    patient_retention_settings: {
      enabled: input.patientRetentionEnabled,
      retention_days: input.retentionDays ? input.retentionDays : null,
    },
  });
};

export {
  getSessionCountBySessionStatus,
  getSessions,
  getSessionsWithPagination,
  getSessionsWithQueryParams,
  getTeamMemberSessionCountBySessionStatus,
} from '@heidi/session/data';

/** @domain session */
export const getSessionsByEhrPatientId = (
  ehr_patient_id: string,
  ehr_provider: EHRProvider
) => {
  return scribeHttpV2.get<SessionsResponse>(`/sessions/`, {
    params: {
      ehr_patient_id,
      ehr_provider,
    },
  });
};

// Carved to `@heidi/session/data` with the session-header modal cores;
// the `@domain session` tag retires with the carve. Re-exported for
// existing importers + `jest.mock` literals.
export { getSessionsByPatientProfileId } from '@heidi/session/data';

/** @domain session */
export const getDictationCleanupModes = (session_id: string) => {
  return scribeHttpV2.get<DictationCleanupModesResponse>(
    `/sessions/${session_id}/documents/dictation-cleanup-modes`
  );
};

/** @domain session */
export const getBackupAudio = (session_id: string) => {
  return scribeHttpV2.get<ListRecordingsResponse>(`/sessions/${session_id}/recordings`, {
    params: {
      expiry: 60 * 60,
    },
  });
};

// Carved to `@heidi/session/data` with the D4 family — the `@domain session`
// tag retires with the carve.
export { submitConsultNoteFeedback } from '@heidi/session/data';

// Carved to `@heidi/session/data` with the D4 family — the `@domain session`
// tag retires with the carve.
export { submitDocumentFeedback } from '@heidi/session/data';

// More of the ARCFE-1087 `@heidi/integrations/ehr` carve — see the
// first block above for the rationale.
export { getClinikoPractitionerInfo } from '@heidi/integrations/ehr';

// V2 Billing
// TRANSFERRED to `@heidi/commerce/data` at D3 (ARCFE-977). Name-intact
// re-export: this was one of workspace/identity-projection's NAMED exclusions
// (ARCFE-956), and the transfer moves the claim to `commerce/billing` in the
// same commit that moves the code.
export { getUserBillingInfo } from '@heidi/commerce/data';

/**
 * Activate clinician evidence trial based on user's current subscription tier.
 * Returns success status and trial info after activation.
 *
 * Backend makes synchronous Stripe API calls and returns the result directly.
 * @domain workspace
 */

/**
 * Activate the practice (Clinician Practice / PRO_PLUS) trial for team signups.
 * Mirrors activateClinicianTrial but targets the dedicated practice endpoint.
 *
 * Backend makes synchronous Stripe API calls and returns the result directly.
 *
 * ponytail: reuses ActivateClinicianTrialResponse — the practice endpoint returns
 * the same shape. Swap to a generated ActivatePracticeTrialResponse once the BE
 * OpenAPI schema lands (`pnpm run schema:dev`).
 * @domain workspace
 */
export const activatePracticeTrial = () => {
  return scribeHttpV2.post<ActivateClinicianTrialResponse>(
    '/billings/activate-practice-trial'
  );
};

/**
 * Cancel reverse (clinician) trial
 * @domain workspace
 */
export const cancelReverseTrial = () => {
  return scribeHttpV2.post<components['schemas']['CancelReverseTrialResponse']>(
    '/billings/cancel-reverse-trial'
  );
};

/**
 * Apply a configured Stripe coupon for the given discount type to the user's active subscription.
 * @domain workspace
 */

// delete all sessions
/** @domain session */
// Carved to `@heidi/session/data` with the D6 family; the consuming delete
// MUTATIONS stay app-side for now (see the D6 scope note in tasks.md) and
// keep resolving through this re-export.
export { deleteAllSessions, deleteEmptySessions } from '@heidi/session/data';

// Snippet Types
// Sourced from the generated OpenAPI schema rather than hand-maintained.
export type Snippet = components['schemas']['SnippetView'];

// Definition moved to `@heidi/workspace/contracts` (IP2, ARCFE-956).
// Import + re-export: this module still uses the name locally (batch upload).
import { type SnippetsResponse } from '@heidi/workspace/contracts';

export type { SnippetsResponse };

export interface UploadRow {
  row: number;
  msg: BulkUploadSnippetErrorType;
  data: Snippet;
}

export enum BulkUploadSnippetErrorType {
  FILE_TOO_LARGE = 'fileTooLarge',
  UNSUPPORTED_FILE_FORMAT = 'unsupportedFileFormat',
  CSV_PARSING_ERROR = 'csvParsingError',
  MISSING_REQUIRED_COLUMNS = 'missingRequiredColumns',
  NO_VALID_ROWS_FOUND = 'noValidRowsFound',
  DUPLICATE_KEY = 'duplicateKey',
  INVALID_CHARS = 'invalidChars',
  KEY_ALREADY_EXISTS = 'keyAlreadyExists',
}
export interface SnippetsUploadResponse {
  valid_rows: UploadRow[];
  error_rows: UploadRow[];
}

export type SingleSnippetResponse = components['schemas']['SingleSnippetResponse'];

// Create Snippets Request
export interface CreateSnippetsRequest {
  snippets: {
    shorthand: string;
    content: string;
  }[];
}

// Update Single Snippet Request
export interface UpdateSingleSnippetRequest {
  new_shorthand: string;
  new_content: string;
}

// Get user snippets — carved to `@heidi/workspace/data` (IP2, ARCFE-956;
// REVIEW → workspace carve-time ruling). Name-intact re-exports.
export { getUserPersonalizations, getUserSnippets } from '@heidi/workspace/data';

/** @domain REVIEW */
export const deletePersonalizationInsight = (
  input: DeletePersonalizationInsightRequest
) => {
  return scribeHttpV2.delete<PersonalizationDataResponse>(`/personalizations`, {
    data: input,
  });
};

/** @domain REVIEW */
export const togglePersonalizationInsight = (
  input: TogglePersonalizationInsightRequest
) => {
  return scribeHttpV2.post<PersonalizationDataResponse>(
    `/personalizations/toggle-insight`,
    { ...input }
  );
};

export type VerbalTriggerIntent = { enabled: true; phrase: string } | { enabled: false };

function verbalTriggerToWireFields(intent: VerbalTriggerIntent | undefined) {
  if (!intent || !intent.enabled) return {};
  return { trigger_phrase: intent.phrase, is_verbal_trigger: true };
}

/** @domain REVIEW */
export const createUserSnippet = (input: {
  shorthand: string;
  content: string;
  verbalTrigger?: VerbalTriggerIntent;
}) => {
  const { shorthand, content, verbalTrigger } = input;
  return scribeHttpV2.post<SingleSnippetResponse>(`/snippets`, {
    shorthand,
    content,
    ...verbalTriggerToWireFields(verbalTrigger),
  });
};

/** @domain REVIEW */
export const createUserSnippetsBatch = (input: {
  snippets: {
    shorthand: string;
    content: string;
    verbalTrigger?: VerbalTriggerIntent;
  }[];
}) => {
  return scribeHttpV2.post<SnippetsResponse>(`/snippets/batch`, {
    snippets: input.snippets.map(({ shorthand, content, verbalTrigger }) => ({
      shorthand,
      content,
      ...verbalTriggerToWireFields(verbalTrigger),
    })),
  });
};

// Upload CSV to create snippets in batch
/** @domain REVIEW */
export const uploadSnippetsBatchCsv = (file: File) => {
  const formData = new FormData();
  formData.append('file', file);

  return scribeHttpV2.post<SnippetsUploadResponse>(`/snippets/batch/upload`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
};

/** @domain REVIEW */
export const updateUserSnippet = (input: {
  snippetId: string;
  newShorthand: string;
  newContent: string;
  verbalTrigger?: VerbalTriggerIntent;
}) => {
  const { snippetId, newShorthand, newContent, verbalTrigger } = input;
  const verbalTriggerFields = !verbalTrigger
    ? {}
    : verbalTrigger.enabled
      ? { new_trigger_phrase: verbalTrigger.phrase, new_is_verbal_trigger: true }
      : { new_is_verbal_trigger: false };

  return scribeHttpV2.patch<SingleSnippetResponse>(`/snippets/${snippetId}`, {
    new_shorthand: newShorthand,
    new_content: newContent,
    ...verbalTriggerFields,
  });
};

// Delete user snippet
/** @domain REVIEW */
export const deleteUserSnippet = (snippetId: string) => {
  return scribeHttpV2.delete<SingleSnippetResponse>(`/snippets/${snippetId}`);
};

//Patient profile

// • GET: /patients/{patient_id} get patient profile

// • POST: /patients create patient profile

// • POST: /patient-profiles:import bulk-create patients from a CSV

// • POST: /patient-profiles:search live keystroke-rate name search
//   POST (not GET) because `q` carries patient-name PHI; query strings
//   would leak it into nginx/ALB access logs and tracing-span URLs.
//   `sessionId` is forwarded as a `?session_id=` query param so the BE
//   permission dep resolves the patient-profile owner from that session
//   (the assistant-acting-for-clinician path). Without it the BE treats
//   the caller as searching their own profiles — wrong tenant for
//   assistants. DEV-12028.

// • PATCH: /patients/{patient_id} update patient profile

// link sessions to patient profile

// unlink sessions to patient profile

// Carved to `@heidi/session/data` with the D6 family (types travel with the fn).
export {
  unlinkMergedSessions,
  type UnlinkMergedSessionsRequest,
  type UnlinkMergedSessionsResponse,
} from '@heidi/session/data';

// Carved to `@heidi/session/data` with the D6 family (types travel with the
// fn). `addToGroup` followed them at ARCFE-1096 — it adds sessions to an
// EXISTING merge group (`:link`), types itself with this same pair, and was
// the merge family's last hub endpoint; the `@domain session` tag retires
// with the carve. Re-exported name-intact so the merge-tab interaction hook
// and the `@/pages/api/scribeAPIV2` compat chain keep resolving; deleted at Z2.
export {
  addToGroup,
  linkMergedSessions,
  type LinkMergedSessionsRequest,
  type LinkMergedSessionsResponse,
} from '@heidi/session/data';

// Confirm the auto-merge for a session under user review. Returns 204 (no body).
// Carved to `@heidi/session/data` with the D6 family — the `@domain session`
// tags retire with the carve (inside the package, the whole file is session's).
export {
  confirmMergedSessions,
  discardMergedSessions,
  revertMergedSessions,
} from '@heidi/session/data';

// delete patient profile
// `deleteLinkedSessions` defaults to `true` to preserve the historical
// cascade behavior for any caller (incl. mobile) that doesn't pass it.

// delete multiple patient profiles at once
// `deleteLinkedSessions` defaults to `true` to preserve the historical cascade
// behavior for any caller that doesn't pass it. Mirrors `deletePatientProfile`.

// merge duplicate patient profiles into one canonical profile
// • POST: /patient-profiles:merge-profiles
// Every session and document from each source profile re-points onto the
// target (the profile to keep); each source is then soft-deleted.

// team member patient profile
// • GET: /teams/{team_id}/members/{member_id}/patient-profiles
/** @domain workspace */
export const getMemberPatientProfile = (teamId: string, memberId: string) => {
  return scribeHttpV2.get<GetPatientProfilesResponse>(
    `/teams/${teamId}/members/${memberId}/patient-profiles`
  );
};

// • POST: /teams/{team_id}/members/{member_id}/patient  create patient profile for team member
/** @domain workspace */
export const postMemberPatientProfile = (
  teamId: string,
  memberId: string,
  patientProfile: CreatePatientProfileRequest
) => {
  return scribeHttpV2.post<CreatePatientProfileResponse>(
    `/teams/${teamId}/members/${memberId}/patient-profiles/`,
    patientProfile
  );
};

// • PATCH: /teams/{team_id}/members/{member_id}/patient update patient profile for team member
/** @domain workspace */
export const patchMemberPatientProfile = (
  teamId: string,
  memberId: string,
  patientProfileId: string,
  patientProfile: UpdatePatientProfileRequest
) => {
  return scribeHttpV2.patch<UpdatePatientProfileResponse>(
    `/teams/${teamId}/members/${memberId}/patient-profiles/${patientProfileId}`,
    patientProfile
  );
};

// • DELETE: /teams/{team_id}/members/{member_id}/patient
/** @domain workspace */
export const deleteMemberPatientProfile = (
  teamId: string,
  memberId: string,
  patientProfileId: string
) => {
  return scribeHttpV2.delete(
    `/teams/${teamId}/members/${memberId}/patient-profiles/${patientProfileId}`
  );
};

// • POST: /teams/{team_id}/members/{member_id}/patient-profiles/{patient_profile_id}/sessions link sessions to patient profile
/** @domain workspace */
export const postLinkSessionsToPatientProfile = (
  teamId: string,
  memberId: string,
  patientProfileId: string,
  sessionIds: LinkPatientSessionsRequest
) => {
  return scribeHttpV2.post(
    `/teams/${teamId}/members/${memberId}/patient-profiles/${patientProfileId}/sessions`,
    sessionIds
  );
};

// • DELETE: /teams/{team_id}/members/{member_id}/patient-profiles/{patient_profile_id}/sessions unlink sessions to patient profile
/** @domain workspace */
export const deleteUnLinkSessionsToPatientProfile = (
  teamId: string,
  memberId: string,
  patientProfileId: string,
  sessionIds: UnlinkPatientSessionsRequest
) => {
  return scribeHttpV2.delete(
    `/teams/${teamId}/members/${memberId}/patient-profiles/${patientProfileId}/sessions`,
    { data: sessionIds }
  );
};

/** @domain REVIEW */
export const getUserInmails = () => {
  return scribeHttpV2.get<GetInmailsResponse>(`/notifications/inmails`);
};

/** @domain REVIEW */
export const createUserInmail = (data: CreateUserInmailRequest) => {
  return scribeHttpV2.post<UserInmailResponse>(`/notifications/inmails/`, data);
};

/** @domain REVIEW */
export const readAllNotifications = () => {
  return scribeHttpV2.post(`/notifications/inmails/read`);
};

/** @domain REVIEW */
export const deleteAllNotifications = () => {
  return scribeHttpV2.delete(`/notifications/inmails:all`);
};

/** @domain REVIEW */
export const deleteAllReadNotifications = () => {
  return scribeHttpV2.delete(`/notifications/inmails:read`);
};

/** @domain REVIEW */
export const deleteNotification = (notificationId: string) => {
  return scribeHttpV2.delete(`/notifications/inmails/${notificationId}`);
};

/** @domain REVIEW */
export const readNotification = (notificationId: string) => {
  return scribeHttpV2.post(`/notifications/inmails/${notificationId}/read`);
};

/** @domain REVIEW */
export const unreadNotification = (notificationId: string) => {
  return scribeHttpV2.post(`/notifications/inmails/${notificationId}/unread`);
};

/** @domain REVIEW */
export const getNotificationPreferences = () => {
  return scribeHttpV2.get<GetUserNotificationPreferencesResponse>(
    `/notifications/preferences`
  );
};

// Carved to `@heidi/session/data` with the D4 family — the `@domain session`
// tag retires with the carve.
export { updateConsultNote } from '@heidi/session/data';

// Carved to `@heidi/workspace/data` (OT3 slice 2, ARCFE-955); re-exported
// name-intact so no importer or jest.mock literal moves. Z1 rewrites them.
export { getRoles } from '@heidi/workspace/data';

// Carved to `@heidi/workspace/data` (IP3, ARCFE-970; the P0.4 org-teams
// ruling executed). Name-intact re-export.
export { updateUserRole } from '@heidi/workspace/data';
// Commerce D1 carve (ARCFE-972): the products / subscription / trials
// endpoints moved to `@heidi/commerce/data` over the same E5 registry client.
// Name-intact re-exports so no importer or jest.mock literal moves; Z1
// rewrites them. Their `@domain REVIEW`/`workspace` tags were ruled
// `@domain commerce` at carve — each serves exactly one commerce-claimed hook
// inside the 2.13-ratified boundary. The three billing READS stay in this hub
// until the `commerce/billing` transfer PR, which moves them together with
// their workspace/identity baseline entries (design D4).
export {
  activateClinicianTrial,
  applySubscriptionDiscount,
  changePlan,
  createAddOnProductCheckoutSession,
  createCheckoutSession,
  extendReverseTrial,
  getAddOnProducts,
  getGamifyTrialExtension,
  getProducts,
  revertReverseTrial,
  updateSubscriptionWithAddOnProduct,
} from '@heidi/commerce/data';

// Carved to `@heidi/session/data` at D5 (ARCFE-990); name-intact re-export.
export { getContextDocuments } from '@heidi/session/data';

// The two session-to-patient-document LINK WRITES carved to
// `@heidi/session/data` on 2026-08-15 (ARCFE-1076, program task 2.11), after
// the program owner resolved `session-patient-document-link-owner` in
// SESSION's favour on the resource-owner rule. Bodies moved verbatim over the
// same `scribeHttpV2` instance through the `@heidi/api/client` registry;
// name-intact re-exports so no importer or `jest.mock` literal moves.
export {
  linkPatientDocumentsToSession,
  unlinkPatientDocumentFromSession,
} from '@heidi/session/data';

// The READ stays here: its hook (`useQuerySessionPatientDocumentLinks`) is
// app-bodied and task 2.10 ticked without touching it, so carving the
// transport alone would leave a re-export serving one app hook for no gain.
/** @domain session */
export const getSessionPatientDocumentLinks = (sessionId: string) => {
  return scribeHttpV2.get<SessionPatientDocumentLinkListResponse>(
    `/sessions/${sessionId}/patient-documents`
  );
};

// The seven session clinical-codes endpoints carved out to
// `@heidi/coding/data` (OpenSpec monorepo-wave-2-coding-pilot · D1/D2). Their
// bodies moved verbatim to `packages/coding/src/data/api/session-codes-api.ts`
// and read the same `scribeHttpV2` singleton through the `@heidi/api/client`
// registry (E5, ARCFE-805) — same instance, same interceptors. Re-exported
// under their hub names so every existing importer and `jest.mock` literal is
// untouched; Z1 rewrites them and Z2 drops these lines.
//
// `searchClinicalCodes` is the one rename: the door already carries the
// SCC-service search of the same name, so the carved fn is
// `searchSessionClinicalCodes` and is aliased back here.
export {
  addNewClinicalCode,
  confirmClinicalCode,
  getSessionClinicalCodes,
  rejectClinicalCode,
  resetClinicalCodesQuota,
  searchSessionClinicalCodes as searchClinicalCodes,
  unconfirmClinicalCode,
} from '@heidi/coding/data';

// Problem-Based Charting APIs
/** @domain session */
export const getSessionProblems = (sessionId: string) => {
  return scribeHttpV2.get<SessionProblemsResponse>(
    `/sessions/${encodeURIComponent(sessionId)}/problems`
  );
};

/** @domain session */
export const confirmSessionProblems = (input: {
  sessionId: string;
  problems: ConfirmProblemRequest[];
}) => {
  const { sessionId, problems } = input;
  return scribeHttpV2.post<ConfirmProblemResponse>(
    `/sessions/${encodeURIComponent(sessionId)}/problems/confirm`,
    { problems }
  );
};

/** @domain session */
export const pushCodesToEHR = (
  sessionId: string,
  options?: { writeToChart?: boolean }
) => {
  return scribeHttpV2.post<{
    success: boolean;
    diagnoses_pushed: number;
    problems_created: number;
    message: string;
  }>(`/sessions/${encodeURIComponent(sessionId)}/problems/push-codes`, {
    write_to_chart: options?.writeToChart ?? true,
  });
};

// POST
// /api/v2/ml-scribe/sessions/{session_id}/clinical-codes/add-new-code
// Add New Code

// E/M Level Determinator
/** @domain session */
export const confirmEmLevelDeterminator = (input: {
  sessionId: string;
  patient_type: EmPatientType;
  code: string;
  determination_method: string;
}) => {
  const { sessionId, ...body } = input;
  return scribeHttpV2.post<EmLevelDeterminatorResponse>(
    `/sessions/${encodeURIComponent(sessionId)}/em-level-determinator/confirm`,
    body
  );
};

/** @domain session */
export const removeEmLevelDeterminator = (sessionId: string) => {
  return scribeHttpV2.post(
    `/sessions/${encodeURIComponent(sessionId)}/em-level-determinator/remove`
  );
};

/** @domain REVIEW */
export const getEhrAuthUrl = ({ profileName, region }: GenerateEhrAuthUrlParams) => {
  const baseUrl = region ? getDomains(region)?.baseV2Url : undefined;

  const url = baseUrl
    ? `${baseUrl}ehr-launch/authentication/authorization-url`
    : `/ehr-launch/authentication/authorization-url`;

  return scribeHttpV2.get<GetEHRLaunchAuthorizationURLResponse>(url, {
    params: {
      profile_name: profileName,
    },
  });
};

/** @domain REVIEW */
export const generateEhrLaunchToken = ({
  profileName,
  code,
  redirectUri,
  user,
  region,
}: GenerateEhrLaunchTokenParams) => {
  const baseUrl = region ? getDomains(region)?.baseV2Url : undefined;

  const url = baseUrl
    ? `${baseUrl}ehr-launch/authentication/access-token`
    : `/ehr-launch/authentication/access-token`;

  return scribeHttpV2.get<GetEHRLaunchAccessTokenDetailsResponse>(url, {
    params: {
      internal_id: user,
      authorization_code: code,
      profile_name: profileName,
      redirect_uri: redirectUri,
    },
  });
};

/** @domain integrations */
export const getEhrLaunchPatient = ({
  accessToken,
  patientId,
  ehrProvider,
  profileName,
}: {
  accessToken: string;
  patientId: string;
  profileName?: string | null;
  ehrProvider: EHRProvider;
}) => {
  return scribeHttpV2.get<GetEHRLaunchPatientDetailsResponse>(`/ehr-launch/patient`, {
    params: {
      access_token: accessToken,
      patient_identifier: patientId,
      ehr_provider: ehrProvider,
      profile_name: profileName,
    },
  });
};

/** @domain REVIEW */
export const getFeatureFlags = () => {
  return scribeHttpV2.get<GetAllFeatureFlagsResponse>(`/feature-flag`);
};

// Carved to `@heidi/session/data` with the D2 transcript family (see above).
export { getLiveTranscript } from '@heidi/session/data';

// Moved to `@heidi/session/data` with the D3 family (tag retires with the
// carve); re-exported for existing importers + `jest.mock` literals.
export { postCreateSessionV2 } from '@heidi/session/data';

/** @domain session */
// Moved to `@heidi/session/data` with the D3 family (tag retires with the
// carve); re-exported for existing importers + `jest.mock` literals.
export { postBulkCreateSessions } from '@heidi/session/data';

export interface GetRepeatReviewsResponse {
  requests: RepeatRequest[];
}

/** @domain REVIEW */
export const getRepeatReviews = () => {
  return scribeHttpV2.get<GetRepeatReviewsResponse>('/repeat-reviews');
};

/** @domain REVIEW */
export const updateRepeatReviewStatus = (
  requestId: string,
  // `actioned_by` is intentionally snake_case to match the ml-scribe repeat-reviews API.
  data: { status: RepeatStatus; actioned_by?: string }
) => {
  return scribeHttpV2.patch<RepeatRequest>(`/repeat-reviews/${requestId}`, data);
};

// Triage-review endpoints (mirror repeat-reviews; richer response shape).
// Returned wire shape is `TriageReviewWire` from triage-adapter.ts.
import type { TriageReviewWire } from '@/features/triage/triage-adapter';

export interface GetTriageReviewsResponse {
  requests: TriageReviewWire[];
}

/** @domain REVIEW */
export const getTriageReviews = () => {
  return scribeHttpV2.get<GetTriageReviewsResponse>('/triage-reviews');
};

/** @domain REVIEW */
export const getTriageReview = (requestId: string) => {
  return scribeHttpV2.get<TriageReviewWire>(`/triage-reviews/${requestId}`);
};

export {
  InviteUsersToTeams,
  acceptTeamInvitation,
  clearOrganizationSessionTimeout,
  createTeam,
  createTeamInvitationLink,
  createTeamInvitationLinkWithRoleIds,
  deleteOrganizationTeamMember,
  deleteTeam,
  deleteTeamMember,
  getJoinedOrganization,
  getJoinedTeams,
  getLogoutEligibleCount,
  getOrganizationByOrganizationId,
  getOrganizationLogoutJob,
  getOrganizationMembers,
  getTeamAnalyticsDashboard,
  getTeamBillingDetails,
  getTeamDetails,
  getTeamMembersWithRelationship,
  logoutAllMatchingOrganizationMembers,
  logoutOrganizationMembersEverywhere,
  moveOrganizationMemberTeam,
  resendTeamInvitation,
  searchOrganizationMembers,
  setOrganizationSessionTimeout,
  toggleTeamConsent,
  toggleTeamMfa,
  toggleTeamReviewConfirmation,
  toggleTeamSessionRetention,
  toggleTeamSessionReviewWithRoleIds,
  updateOrganizationById,
  updateOrganizationMemberRole,
  updateTeam,
  updateTeamSettings,
} from '@heidi/workspace/data';

// Team-scoped SESSION views — deliberately NOT carved with OT2a: their
// response types are Session-owned composite contracts and
// `workspace -> session` is not an admitted edge (P0.4-class ruling
// pending with scribe-pod).
export type TeamSessionsParams = {
  page?: number;
  page_size?: number;
  /** Comma-separated session statuses: EMPTY, DRAFT, REVIEWED, APPROVED, SENT */
  status?: string;
  assign_to?: 'all' | 'unassigned' | 'me';
  /** Filter to sessions assigned to this assistant (kinde_user_id). Used with assign_to when filtering by assignee. */
  assign_to_user_id?: string;
  clinician_ids?: string;
  q?: string;
  /** Backend accepts only mostRecent, oldest, name, urgency (use 'mostRecent' for "received" ordering). */
  sort?: 'mostRecent' | 'oldest' | 'name' | 'urgency';
  include_counts?: boolean;
  include_total_count?: boolean;
};

/** Session item from team sessions API (snake_case from backend) */
export type TeamSessionItem = SimpleSessionResponse & {
  user_name: string;
  kinde_user_id: string;
  user_title?: string | null;
  user_first_name?: string | null;
  user_last_name?: string | null;
};

/** Response from GET /teams/{team_id}/sessions */
export type TeamSessionsResponse = {
  sessions: TeamSessionItem[];
  has_next_page: boolean;
  total_count?: number | null;
  total_non_active?: number | null;
  total_active?: number | null;
  total_done?: number | null;
};

/** @domain workspace */
export const getSessionsByTeamMemberId = (input: {
  teamId: string;
  memberId: string;
  pageSize?: number;
  lastSessionCreatedAt?: string | null;
}) => {
  return scribeHttpV2.get<SessionsResponse>(
    `/teams/${input.teamId}/members/${input.memberId}/sessions`,
    {
      params: {
        page_size: input.pageSize,
        last_session_created_at: input.lastSessionCreatedAt,
      },
    }
  );
};

/** @domain workspace */
export const getTeamSessionsForAssistantView = (
  teamId: string,
  params: TeamSessionsParams
) => {
  return scribeHttpV2.get<TeamSessionsResponse>(
    `/teams/${encodeURIComponent(teamId)}/sessions`,
    {
      params: {
        page: params.page ?? 1,
        page_size: params.page_size ?? 100,
        ...(params.status != null && params.status !== '' && { status: params.status }),
        ...(params.assign_to && { assign_to: params.assign_to }),
        ...(params.assign_to_user_id && { assign_to_user_id: params.assign_to_user_id }),
        ...(params.clinician_ids && { clinician_ids: params.clinician_ids }),
        ...(params.q != null && params.q !== '' && { q: params.q }),
        ...(params.sort && { sort: params.sort }),
        ...(params.include_counts != null && {
          include_counts: params.include_counts,
        }),
        ...(params.include_total_count != null && {
          include_total_count: params.include_total_count,
        }),
      },
    }
  );
};

/** @domain workspace */
// Moved to `@heidi/workspace/data` (Wave 2 · W, ARCFE-801).
export { getOrganizationTeams } from '@heidi/workspace/data';

// P0.4 ruling (monorepo-tasks-data-core, 2026-08-04): Session-owned — sole
// caller chain is panel.ts -> scribeRecordModel.ts, state lives in a
// session-panel atom. Deliberately NOT carved with D1 (ARCFE-937); the tag
// moves it out of the tasks family census to match the ratified ownership.
/** @domain session */
export const generateSessionClinicalTasks = (sessionId: string) => {
  return scribeHttpV2.post<GetClinicalTasksResponse>(
    `/clinical-tasks/session/${sessionId}/generate`,
    {}
  );
};

// Clinical-task API fns — carved to `@heidi/tasks/data` (D1, ARCFE-937; the
// teams/organization/entitlements E5 precedent). Name-intact re-exports so no
// importer or `jest.mock` literal moves. `deleteAllSessionClinicalTasks` was
// deleted, not moved (P0.4: zero callers); `generateSessionClinicalTasks`
// stays above under `@domain session` (P0.4 ruling).
export { type BulkClinicalTasksResponse } from '@heidi/tasks/contracts';
export {
  assignClinicalTask,
  bulkDeleteClinicalTasks,
  bulkUpdateClinicalTasks,
  createClinicalTask,
  deleteClinicalTask,
  getClinicalTaskById,
  getClinicalTasks,
  unassignClinicalTask,
  updateClinicalTask,
} from '@heidi/tasks/data';

// ---------------------------------------------------------------------------
// Apollo prescription-category slice — Phase 4 (gated by
// `release-apollo-prescription-category`). Mirrors the clinical-task client
// shape: each function is a thin wrapper around scribeHttpV2. See
// `apis/v2/prescription/endpoints.py` in ml-scribe for the backend contract.
// ---------------------------------------------------------------------------

/** @domain REVIEW */
export const createPrescription = (data: CreatePrescriptionRequest) => {
  return scribeHttpV2.post<PrescriptionResponse>('/prescriptions', data);
};

/** @domain REVIEW */
export const getPrescription = (prescriptionId: string) => {
  return scribeHttpV2.get<PrescriptionResponse>(`/prescriptions/${prescriptionId}`);
};

/** @domain REVIEW */
export const updatePrescription = (
  prescriptionId: string,
  data: UpdatePrescriptionRequest
) => {
  return scribeHttpV2.patch<PrescriptionResponse>(
    `/prescriptions/${prescriptionId}`,
    data
  );
};

/** @domain REVIEW */
export const deletePrescription = (prescriptionId: string) => {
  return scribeHttpV2.delete(`/prescriptions/${prescriptionId}`);
};

/** @domain session */
export const getPatientContext = (sessionId: string) => {
  return scribeHttpV2.get<PatientContextResponse>(
    `/sessions/${sessionId}/patient-context`
  );
};

/** @domain workspace */
export const recordGamifyTemplateSelected = () => {
  return scribeHttpV2.post<void>('/user/gamify-trial-extension/record-template-selected');
};

/** @domain workspace */
export const recordGamifySourceControlUsed = () => {
  return scribeHttpV2.post<void>(
    '/user/gamify-trial-extension/record-source-control-used'
  );
};

/** @domain workspace */
export const markPostNoteQualityTaskCompleted = (taskId: ImproveNotesTaskId) => {
  return scribeHttpV2.patch<void>(`/user/post-note-quality/tasks/${taskId}`);
};

/** @domain workspace */
export const dismissPostNoteQualityPanel = () => {
  return scribeHttpV2.post<void>('/user/post-note-quality:dismiss');
};

/**
 * GRO-3284: marks the "Improve your notes" panel as seen so subsequent opens
 * report `first_view: false`. Idempotent on the backend.
 * @domain workspace
 */
export const markPostNoteQualityPanelSeen = () => {
  return scribeHttpV2.post<void>('/user/post-note-quality:mark-seen');
};

// Carved to `@heidi/templates/data` at ARCFE-1015 with a recorded DOMAIN
// RULING (was `@domain REVIEW`).
export { getUploadTemplateURL } from '@heidi/templates/data';

/** @domain REVIEW */
export const getExportAssetUploadUrl = (request: ExportAssetUrlRequest) => {
  return scribeHttpV2.post<ExportAssetUrlResponse>(
    '/export-assets/presigned-url',
    request
  );
};

/** @domain REVIEW */
export const getExportAssetDownloadUrl = (objectKey: string) => {
  return scribeHttpV2.post<ExportAssetUrlResponse>(
    `/export-assets/presigned-url/download?object_key=${encodeURIComponent(objectKey)}`
  );
};

/** @domain REVIEW */
export const getFilledPDFTemplate = (documentId: string) => {
  return scribeHttpV2.post<string>(`/pdf-template/${documentId}`);
};

/** @domain REVIEW */
export const getFilledDocxTemplate = (documentId: string) => {
  return scribeHttpV2.post<string>(`/pdf-template/${documentId}/export-docx`);
};

/** @domain REVIEW */
export const getFilledPdfFields = (documentId: string) => {
  return scribeHttpV2.get<GetFilledPdfFieldsResponse>(
    `/pdf-document/${documentId}/fields`
  );
};

/** @domain REVIEW */
export const updatePdfFields = (documentId: string, fields: FieldUpdate[]) => {
  return scribeHttpV2.patch<UpdatePdfFieldsResponse>(
    `/pdf-document/${documentId}/fields`,
    { fields }
  );
};

/** @domain session */
// Carved to `@heidi/session/data` at ARCFE-1004 — WITHOUT its transcript-
// deleted guard, which reads the capture-boundary transcript module and so
// stays app-side, injected into the hook and run before the request.
export { createPDFDocument } from '@heidi/session/data';

/** @domain coding */
export const submitClinicalCodeFeedback = (
  sessionId: string,
  submitClinicalCodeFeedbackRequest: SubmitClinicalCodeFeedbackRequest
) => {
  return scribeHttpV2.post<SessionClinicalCodesResponse>(
    `/sessions/${sessionId}/clinical-codes/feedback`,
    submitClinicalCodeFeedbackRequest
  );
};

/** @domain REVIEW */
export const getPdf = async (exportPdfRequest: ExportPdfRequest) => {
  const response = await scribeHttpV2.post<Blob>(`/export-pdf`, exportPdfRequest, {
    responseType: 'blob',
  });

  const blob = response.data;
  const contentDisposition = response.headers['content-disposition'] as
    | string
    | undefined;
  let filename: string | undefined = exportPdfRequest.file_name || 'download.pdf';
  if (contentDisposition) {
    const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
    if (filenameMatch && filenameMatch.at(1)) {
      filename = filenameMatch.at(1);
    }
  }

  if (!filename) {
    throw new Error('Filename not found');
  }

  return { blob, filename };
};

/** @domain REVIEW */
export const getDocx = async (exportDocxRequest: ExportDocxRequest) => {
  const response = await scribeHttpV2.post<Blob>(`/export-docx`, exportDocxRequest, {
    responseType: 'blob',
  });

  const blob = response.data;
  const contentDisposition = response.headers['content-disposition'] as
    | string
    | undefined;
  let filename: string | undefined = exportDocxRequest.file_name || 'download.docx';
  if (contentDisposition) {
    const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
    if (filenameMatch && filenameMatch.at(1)) {
      filename = filenameMatch.at(1);
    }
  }

  if (!filename) {
    throw new Error('Filename not found');
  }

  return { blob, filename };
};

// Carved to `@heidi/integrations/connected-apps` at ARCFE-1087 — the 9
// `/integrations/**` endpoints that CHOOSE or PROVISION an integration rather
// than use one. `updateIntegrationOnboardingSessionStep` is `@domain REVIEW`,
// claimed by no family, and stays here.
export { requestEHRIntegration } from '@heidi/integrations/connected-apps';

export enum IntegrationAgentOnlineStatus {
  Online = 'online',
  Offline = 'offline',
  /** Frontend-only: endpoint returned 404 — no agent registered for this user. */
  NotConfigured = 'not_configured',
}

export interface IntegrationAgentStatusResponse {
  /** Backend only returns Online or Offline; NotConfigured is inferred from a 404. */
  status: IntegrationAgentOnlineStatus.Online | IntegrationAgentOnlineStatus.Offline;
  ehr_system: string | null;
  agent_version: string | null;
  clinic_name: string | null;
  label: string | null;
  last_seen: string | null;
  ehr_connected: boolean;
  ehr_version: string | null;
}

/** @domain REVIEW */
export const getIntegrationAgentStatus = () => {
  return scribeHttpV2.get<IntegrationAgentStatusResponse>(
    '/integration-agent/agents/status'
  );
};

// (`userSelfOnboard` carved to `@heidi/workspace/data` at D1, ARCFE-1037 —
// re-exported name-intact next to `toggleUserMfa` above.)

// ─────────────────────────────────────────────────────────────────────────────
// Carved to `@heidi/calls/data` with the `operations`, `ehr-links` and
// `conversations` families (D1, ARCFE-1050). The `@domain calls` tags retire
// with the carve; re-exported name-intact so existing importers and their
// `jest.mock` literals keep resolving until Z1's codemod.
//
// Transport is unchanged: the package fns resolve `scribeHttpV2` through the
// `@heidi/api/client` registry, which app wiring sets to this module's own
// singleton. A suite that mocks `@/utils/scribeHTTP` no longer intercepts
// these — it must ALSO set a registry double (`setScribeHttpClients`).
//
// DELETED, not carved (P0 zero-consumer finding, the
// `deleteAllSessionClinicalTasks` precedent): `checkBlockedNumber`,
// `bulkAddBlockedNumbers` and `getCallAudioUrl` had no production caller —
// only this hub, its `src/pages/api` compat re-export and the hub test.
// ─────────────────────────────────────────────────────────────────────────────
export {
  addBlockedNumber,
  bulkCreateCall,
  createCall,
  createCallsEHRCredentials,
  deleteBlockedNumber,
  deleteCall,
  deleteCallsPatient,
  deleteCallsUserEHRCredentials,
  getBlockedNumbers,
  getCall,
  getCallTranslation,
  getCalls,
  getCallsEHRAppointmentTypes,
  getCallsEHRClinicians,
  getCallsEHRClinics,
  getCallsPatientAppointments,
  getCallsPatients,
  getCallsUserEHRCredentials,
  getConversationDetail,
  getConversations,
  getInboxCallDetail,
  getOperationalBreakdowns,
  getOperationalDaily,
  getOperationalSummary,
  markConversationRead,
  patchCall,
  postInboxWebSocketTicket,
  refreshCallsEHRAppointmentTypes,
  refreshCallsEHRClinicians,
  refreshCallsEHRClinics,
  sendInboxMessage,
  updateCallsEHRAppointmentType,
  updateCallsEHRClinic,
  updateCallsEHRClinician,
  updateConversation,
  uploadCallsPatientsCsv,
  uploadInboxMedia,
} from '@heidi/calls/data';

export type {
  BlockedNumber,
  BlockedNumberListResponse,
  BlockedVia,
  CreateBlockedNumberPayload,
  CreateBlockedNumberResponse,
  InboxWebSocketTicketResponse,
  PatchCallPayload,
} from '@heidi/calls/contracts';

// The three flag-wrapping entries below call into the package under aliases so
// the hub can keep the ORIGINAL exported names. `guardrailsV2RequestConfig` is
// imported unaliased — the hub's own export is `getGuardrailsV2RequestConfig`,
// so there is no collision.
import type { UpdateAgentRequest } from '@heidi/calls/contracts';
import {
  checkGuardrail as callsCheckGuardrail,
  commitAgentFromObjective as callsCommitAgentFromObjective,
  updateCallAgent as callsUpdateCallAgent,
  guardrailsV2RequestConfig,
} from '@heidi/calls/data';

// ─────────────────────────────────────────────────────────────────────────────
// Carved to `@heidi/calls/data` with the `calls/agents` family (D1,
// ARCFE-1097) — the fifth and last family of this lane. The `@domain calls`
// tags retire with the carve; re-exported name-intact so existing importers
// and their `jest.mock` literals keep resolving until Z1's codemod.
//
// Transport is unchanged: the package fns resolve `scribeHttpV2` through the
// `@heidi/api/client` registry, which app wiring sets to this module's own
// singleton. A suite that mocks `@/utils/scribeHTTP` no longer intercepts
// these — it must ALSO set a registry double (`setScribeHttpClients`).
//
// DELETED, not carved (P0.2 zero-consumer finding, the
// `deleteAllSessionClinicalTasks` precedent, re-verified at HEAD before
// deletion): `generateAgentPreview` and `getLatestAgentBuilderSession` had no
// production caller — only this hub and its `src/pages/api` compat re-export,
// and neither was exercised even by the hub test. Both were already ruled
// dead by the child change's own D1 line. `PreviewConversationTurn` survives
// them in `@heidi/calls/contracts` because `src/atoms/callsAtoms.ts`
// `simulatedTurnsAtom` is typed with it.
//
// THREE ENTRIES WRAP INSTEAD OF RE-EXPORTING, and that is the ARCFE-1060
// ruling rather than an inconsistency. `checkGuardrail`, `updateCallAgent` and
// `commitAgentFromObjective` branch on `IS_COMMS_GUARDRAILS_V2_ENABLED`, an
// app flag no package may read. It crosses as a GETTER, called at request time
// exactly where these bodies called it, because the flag resolves through
// `posthog.isFeatureEnabled` — which emits a `$feature_flag_called` exposure
// event on read, so resolving it eagerly would fire exposures for requests
// that never reach the branch. The public signatures below are byte-identical
// to the pre-carve ones, so no caller changes.
//
// THE SECOND TRANSPORT CLASS IS NOW CARVED TOO, and its five names are in the
// same re-export list below rather than in a block of their own.
// `simulateAgentStream` (SSE) and the text-chat quartet
// (`getCallsChatApiBaseUrl`, `createTextChatSession`, `getAgentGreeting`,
// `getTextChatStreamUrl`) live in `packages/calls/src/data/api/agent-chat.ts`.
// The E5 registry does not reach them — they use raw `fetch` and an SSE client
// — so three app-owned inputs cross a seam instead: the chat service's base
// URL (an env read), the v2 stream headers, and the SSE client itself, all
// supplied by `src/utils/calls-data-wiring.ts` and all called at request time.
// Their `@domain REVIEW` tags retire with the move: the tags were honest while
// the bodies were here, and the endpoint census still cannot see a `fetch`,
// which is why `calls-transport-inventory.test.ts` — not a tag — is what keeps
// them from coming back.
//
// `getV2StreamHeaders` did NOT move and is not this family's: it is the
// generic v2 stream-header builder that every raw-fetch streaming caller
// shares. It stays below with its own tag and crosses the seam as a port.
// ─────────────────────────────────────────────────────────────────────────────
export {
  addCustomAgentScenario,
  createAgentBuilderSession,
  createTextChatSession,
  deleteCallAgent,
  generateKnowledgeBase,
  getAgentGreeting,
  getAgentPredictedParameters,
  getAgentQARun,
  getCallAgent,
  getCallAgentWebRTCToken,
  getCallAgents,
  getCallVoiceCatalog,
  getCallsChatApiBaseUrl,
  getLastAgentSimulationRun,
  getTextChatStreamUrl,
  listAgentScenarios,
  previewCallVoice,
  purchaseCallAgentPhoneNumber,
  recompileAgent,
  refreshAgentScenarios,
  regenerateAgentScenarios,
  runAgentQA,
  searchClinics,
  sendAgentBuilderChat,
  simulateAgentStream,
} from '@heidi/calls/data';

export type {
  AgentBuilderChatResponse,
  AgentScenario,
  AgentSimulationRun,
  PreviewConversationTurn,
} from '@heidi/calls/contracts';

/**
 * @domain REVIEW
 *
 * The app's flag read for the guardrails-v2 header. The header logic itself
 * lives in `@heidi/calls/data`; this entry supplies the one thing the package
 * may not have — `IS_COMMS_GUARDRAILS_V2_ENABLED` — and keeps the exported
 * name its compat shim and its own test already depend on.
 */
export const getGuardrailsV2RequestConfig = () =>
  guardrailsV2RequestConfig(IS_COMMS_GUARDRAILS_V2_ENABLED);

/** @domain calls */
export const commitAgentFromObjective = (
  params: Parameters<typeof callsCommitAgentFromObjective>[0],
  options?: Parameters<typeof callsCommitAgentFromObjective>[2]
) => callsCommitAgentFromObjective(params, IS_COMMS_GUARDRAILS_V2_ENABLED, options);

/** @domain calls */
export const checkGuardrail = (field: string, value: string) =>
  callsCheckGuardrail(field, value, IS_COMMS_GUARDRAILS_V2_ENABLED);

/** @domain calls */
export const updateCallAgent = (agentId: string, agent: UpdateAgentRequest) =>
  callsUpdateCallAgent(agentId, agent, IS_COMMS_GUARDRAILS_V2_ENABLED);

// MOVED to `@/utils/http/scribeSseHeaders`, beside `buildScribeSseHeaders`,
// and re-exported name-intact so the compat shim, the text-chat preview and
// both hub suites keep resolving it here. It is not an endpoint and never was
// — it is the header builder every raw-`fetch` streaming caller shares — and
// it had to leave a module that only ever shrinks so `calls-data-wiring.ts`
// could inject it into `@heidi/calls/data` without becoming a new importer of
// this hub.
export { getV2StreamHeaders } from '@/utils/http/scribeSseHeaders';

// add on products

// TRANSFERRED to `@heidi/commerce/data` at D3 (ARCFE-977) — see the note on
// `getUserBillingInfo` above.
export { getUserAddOnBillingInfo } from '@heidi/commerce/data';

/** @domain REVIEW */
export const postEvidenceUpgradeCheckout = (params: CreateEvidenceCheckoutRequest) => {
  return scribeHttpV2.post<EvidenceCheckoutResponse>(`/chat/billing/checkout`, params);
};

/* Evidence Integrations */
/** @domain evidence */
export const getEvidenceIntegrations = () => {
  return scribeHttpV2.get<components['schemas']['IntegrationsListResponse']>(
    '/evidence/integrations',
    { params: { publisher_web_search: true } }
  );
};

/* HealthPathways Zone Setup */
// TRANSFERRED to `@heidi/evidence/data` with the `evidence/guidance` carve —
// the two zone transports now run on the `@heidi/api/client` registry and the
// `@domain evidence` tags retire with the bodies. Name-intact re-exports, so
// every importer of this hub (and of the `src/pages/api/scribeAPIV2` compat
// barrel) keeps resolving, and both still return the axios envelope.
export { removeHealthPathwaysZone, setupHealthPathwaysZone } from '@heidi/evidence/data';

// More of the ARCFE-1087 `@heidi/integrations/connected-apps` carve — see the
// first block above for the rationale.
export {
  createIntegrationOnboardingSession,
  deleteIntegrationOnboardingSession,
  getAvailableDirectIntegrations,
  getIntegrationCapabilities,
} from '@heidi/integrations/connected-apps';

/** @domain REVIEW */
export const updateIntegrationOnboardingSessionStep = async ({
  session_id,
  step_id,
  data,
  signal,
}: {
  session_id: string;
  step_id: string;
  data: IntegrationOnboardingStepExecutionRequest;
  signal?: AbortSignal;
}) => {
  const transformedData = {
    ...data,
    payload: data.payload
      ? 'form_data' in data.payload
        ? {
            ...data.payload,
            form_data: transformLocalUrlsToBase64(
              (data.payload.form_data ?? {}) as Record<string, unknown>
            ),
          }
        : data.payload
      : undefined,
  };

  const endpoint = `integrations/onboardings/sessions/${session_id}/step_executions/${step_id}`;
  const { data: result } = signal
    ? await scribeHttpV2.put<IntegrationOnboardingStepExecutionResponse>(
        endpoint,
        transformedData,
        {
          signal,
        }
      )
    : await scribeHttpV2.put<IntegrationOnboardingStepExecutionResponse>(
        endpoint,
        transformedData
      );

  return {
    ...result,
    updated_session: transformOnboardingStepBase64Entries(result.updated_session),
  };
};

// More of the ARCFE-1087 `@heidi/integrations/connected-apps` carve — see the
// first block above for the rationale.
export {
  getIntegrationOnboardingSessionById,
  getIntegrationOnboardingSessions,
} from '@heidi/integrations/connected-apps';

// More of the ARCFE-1087 `@heidi/integrations/ehr` carve — see the first block
// above for the rationale.
export {
  createIntegrationWorkflowSession,
  getCernerNoteTypes,
  getHaikuLaunchInfo,
  getIntegrationAvailableStandardizedSections,
  getIntegrationLinkedConsultNoteInfo,
  getIntegrationLinkedSessionInfo,
  getNoteOptionsForIntegration,
  getSyncDocumentOptions,
  getTemplateSectionMapping,
  patchIntegrationSessionLink,
  replaceTemplateSectionMapping,
  syncDocuments,
  syncNoteAudit,
  syncNoteToIntegration,
  syncNoteWithOptionsToIntegration,
  syncSectionalNoteToIntegration,
  uploadFileToIntegration,
} from '@heidi/integrations/ehr';

// START Template creation record

/** @domain REVIEW */
export const fillFormDemo = (request: FillFormDemoRequest) => {
  return scribeHttpV2.post<FillFormDemoResponse>('/pdf-template-demo/fill-form', request);
};

/** @domain REVIEW */
export const fillFormDemoFromUpload = (request: FillFormDemoFromUploadRequest) => {
  const formData = new FormData();
  formData.append('source', request.source);
  formData.append('file', request.file);
  formData.append('s3_object_key', request.s3_object_key);
  formData.append('form_fields', JSON.stringify(request.form_fields));
  if (request.specialty_name) {
    formData.append('specialty_name', request.specialty_name);
  }
  if (request.country) {
    formData.append('country', request.country);
  }
  if (request.language) {
    formData.append('language', request.language);
  }

  return scribeHttpV2.post<FillFormDemoResponse>(
    '/pdf-template-demo/fill-form-from-upload',
    formData,
    {
      headers: { 'Content-Type': 'multipart/form-data' },
    }
  );
};

/** @domain REVIEW */
export const fillXmlFormDemo = (request: FillXmlFormDemoRequest) => {
  return scribeHttpV2.post<FillXmlFormDemoResponse>(
    '/pdf-template-demo/fill-xml-form',
    request
  );
};

/** @domain REVIEW */
export const fillXmlFormDemoFromUpload = (request: FillXmlFormDemoFromUploadRequest) => {
  const formData = new FormData();
  formData.append('source', request.source);
  formData.append('file', request.file);
  formData.append('xml_source', request.xml_source);
  formData.append('form_fields', JSON.stringify(request.form_fields));
  if (request.specialty_name) {
    formData.append('specialty_name', request.specialty_name);
  }
  if (request.country) {
    formData.append('country', request.country);
  }
  if (request.language) {
    formData.append('language', request.language);
  }

  return scribeHttpV2.post<FillXmlFormDemoResponse>(
    '/pdf-template-demo/fill-xml-form-from-upload',
    formData,
    {
      headers: { 'Content-Type': 'multipart/form-data' },
    }
  );
};

// END TEMPLATE CREATION RECORD

// Carved to `@heidi/session/data` at ARCFE-1096 (the INT-4603 section-link
// PATCH). Its request/response pair travelled with it — the response widens
// `consult_note` with `section_mappings`, so it is not the bare generated
// shape and could not be left behind. `src/types` keeps its own rows for any
// other importer. The `@domain session` tag retires with the carve;
// re-exported name-intact for `useMutatehandleEhrNoteSectionLink` and the
// `@/pages/api/scribeAPIV2` compat chain, deleted at Z2.
export { updateJSONConsultNoteSectionLink } from '@heidi/session/data';

// More of the ARCFE-1087 `@heidi/integrations/ehr` carve — see the
// first block above for the rationale.
export {
  getAvailableEhrSections,
  getHaikuAvailableSections,
  getHaikuLatestRecordingId,
  postPushNoteToHaiku,
} from '@heidi/integrations/ehr';

/** @domain session */
// Carved to `@heidi/session/data` at ARCFE-1001 — the one genuinely-session
// endpoint of the five the feedback family claimed; re-exported name-intact.
export { submitFeedbackV2 } from '@heidi/session/data';

/** @domain session */
// Carved to `@heidi/session/data` with the D6 family.
export { getFeedbackV2 } from '@heidi/session/data';

/** @domain session */
export const submitSafetyIssueTicket = (
  sessionId: string,
  request: SafetyIssueTicketRequest
) => {
  return scribeHttpV2.post<SafetyIssueTicketResponse>(
    `/sessions/${sessionId}/safety-issue-ticket`,
    request
  );
};

/**
 * Submit chat feedback (thumb up/down) — works for both sessionless and in-session chats.
 * Uses the dedicated POST /chat/feedback endpoint which doesn't require a session_id.
 */
export interface ChatFeedbackRequest {
  target_id: string; // Chat ID (24-char hex ObjectId)
  session_id?: string | null;
  message_id?: string | null; // Backend message_id from message_info chunk for linking feedback to specific assistant messages
  feedback: {
    rating: 'thumb_up' | 'thumb_down';
    user_feedback?: string | null;
    reasons?: string[];
    mode?: 'legacy' | 'evidence' | null;
  };
  content_snapshot?: string | null;
}

export interface ChatFeedbackResponse {
  feedback_id: string;
}

/** @domain REVIEW */
export const submitChatFeedback = (request: ChatFeedbackRequest) => {
  return scribeHttpV2.post<ChatFeedbackResponse>('/chat/feedback', request);
};

/** @domain REVIEW */
/** @deprecated Use submitChatFeedback instead */
export const submitEvidenceFeedback = submitChatFeedback;

// More of the ARCFE-1087 `@heidi/integrations/ehr` carve — see the
// first block above for the rationale.
// TWO SIBLINGS OF THESE THREE WERE DELETED, NOT MOVED (ARCFE-1087):
// `getEhrLaunchLatestNote` (`GET .../consult-note/ehr-latest`) and
// `getEhrLaunchPastNotesWithCommonlyUsedNotes` (`GET
// .../consult-note/past-ehr-notes-with-common`) had zero callers anywhere in
// `src/**` or `e2e/**` — only their own declarations and the
// `src/pages/api/scribeAPIV2.ts` re-export list, which is updated in the same
// commit.
export {
  getEhrLaunchCommonlyUsedPastEhrNotes,
  getEhrLaunchPastNoteDetails,
  getEhrLaunchPastNotes,
} from '@heidi/integrations/ehr';

// More of the ARCFE-1087 `@heidi/integrations/connected-apps` carve — see the
// first block above for the rationale.
export {
  createIntegrationContactSalesRequest,
  getIntegrationContactSalesRequest,
} from '@heidi/integrations/connected-apps';

/** @domain workspace */
export const markModalAsViewed = (input: RecordModalViewRequest) => {
  return scribeHttpV2.post<{
    seen_modals: SeenModals;
  }>(`/user/modal-view`, {
    modal_id: input.modal_id,
    increment_count: input.increment_count,
    action: input.action,
    hide_forever: input.hide_forever,
  });
};

/** @domain workspace */
export const updateAutoLinkIntent = (input: UpdateAutoLinkIntentRequest) => {
  return scribeHttpV2.patch<void>(`/user/auto-link-intent`, input);
};

/** @domain workspace */
export const updateAutoExtractIntent = (input: UpdateAutoExtractIntentRequest) => {
  return scribeHttpV2.patch<void>(`/user/auto-extract-intent`, input);
};

// Chat API
/** @domain REVIEW */
export const createChat = (input: CreateChatRequest) => {
  return scribeHttpV2.post<CreateChatResponse>('/chat', input);
};

export interface ContinueChatResponse {
  chat_id: string;
  title: string;
  continued_from_chat_id: string;
  session_id: string | null;
  patient_profile_id: string | null;
}

/** @domain REVIEW */
export const continueChat = (chatId: string) => {
  return scribeHttpV2.post<ContinueChatResponse>(`/chat/${chatId}/continue`);
};

/** @domain REVIEW */
export const storeChatsFromLocalStorage = (body: StoreChatsFromLocalStorageRequest) => {
  return scribeHttpV2.post<StoreChatsFromLocalStorageResponse>('/chat/migrate', body);
};

export interface JurisdictionOptionResponse {
  code: string;
  name: string;
  type?: string | null;
}

/** @domain REVIEW */
export const getJurisdictionsByCountry = (country: string) => {
  return scribeHttpV2.get<JurisdictionOptionResponse[]>('/chat/jurisdictions', {
    params: { country },
  });
};

// Chat Attachments API
export interface ChatAttachmentResponse {
  id: string;
  name: string;
  type: string;
  size: number;
  url: string;
}

/** @domain REVIEW */
export const uploadChatAttachment = ({
  file,
  chatId,
  onProgressUpdate,
  signal,
}: {
  file: File;
  chatId?: string;
  onProgressUpdate?: (progress: number) => void;
  signal?: AbortSignal;
}) => {
  const formData = new FormData();
  formData.append('file', file);

  const params = chatId ? `?chatId=${chatId}` : '';

  return scribeHttpV2.post<ChatAttachmentResponse>(
    `/chat/attachments${params}`,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      signal,
      onUploadProgress: (progressEvent) => {
        if (progressEvent.total && onProgressUpdate) {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          onProgressUpdate(percentCompleted);
        }
      },
    }
  );
};

/** @domain REVIEW */
export const deleteChatAttachment = (attachmentId: string) => {
  return scribeHttpV2.delete(`/chat/attachments?attachmentId=${attachmentId}`);
};

export interface BulkDeleteChatsResponse {
  deleted: string[];
  failed: string[];
}

/** @domain REVIEW */
export const bulkDeleteChats = (data: { ids: string[] }) => {
  return scribeHttpV2.delete<BulkDeleteChatsResponse>('/chat/chats/bulk', { data });
};

// Follow-up Questions API
export interface FollowUpQuestionsRequest {
  chatId: string;
  userQuery: string;
  assistantResponse: string;
  toolCalls?: string[];
  specialty?: string;
  displayLanguage?: string;
}

export interface FollowUpQuestionsResponse {
  questions: FollowUpQuestion[];
}

/** @domain REVIEW */
export const getFollowUpQuestions = (input: FollowUpQuestionsRequest) => {
  return scribeHttpV2.post<FollowUpQuestionsResponse>('/chat/follow-up-questions', input);
};

/** @domain REVIEW */
export const createChatSuggestions = (request: {
  text?: string | null;
  specialty_group_id?: string | null;
  /**
   * Active consult session ID, when suggestions are typed inside a session.
   * Used only for span tagging (heidi.session_id) on the BE; no effect on
   * suggestion generation. Optional — omit when out of session. See EVI-1540.
   */
  session_id?: string | null;
}) => {
  return scribeHttpV2.post<{
    suggestions: Array<{
      content: string;
      type: 'free' | 'pro';
      feature?: string | null;
    }>;
  }>('/chat/suggestions', request);
};

// === Share Chat API ===

export interface ShareChatResponse {
  share_token: string;
  share_url: string;
  preview_question: string;
  preview_response: string;
  chat_title: string;
}

export interface SharedChatMessage {
  role: string;
  content: string;
  timestamp?: string;
  message_id?: string;
  mode?: string;
  original_mode?: string;
  metadata?: Record<string, unknown>;
  attachments?: Array<{
    attachment_id?: string;
    file_name: string;
    content_type?: string;
    file_key?: string;
    url?: string;
  }>;
}

export interface SharedChatResponse {
  // null when create_copy=false (cross-region fetch — frontend will store the copy locally)
  copied_chat_id: string | null;
  title: string;
  messages: SharedChatMessage[];
  mode: string;
  created_at: string;
  temp_user_id?: string | null;
}

export interface SharedChatCopyRequest {
  title: string;
  mode: string;
  messages: SharedChatMessage[];
}

/**
 * Share an Evidence chat and get a shareable link.
 * The link never expires. Recipients get an independent copy of the chat.
 * @domain REVIEW
 */
export const shareChat = (chatId: string) => {
  return scribeHttpV2.post<ShareChatResponse>(`/chat/chats/${chatId}/share`, {});
};

/**
 * Get a shared chat by its share token via the user's local region backend.
 * Creates a copy of the original chat for the recipient (default behaviour).
 * @domain REVIEW
 */
export const getSharedChat = (shareToken: string) => {
  return scribeHttpV2.get<SharedChatResponse>(`/chat/shared/${shareToken}`);
};

/**
 * Get a shared chat from a specific region backend without creating a copy.
 * Used when the URL includes a region prefix (direct routing) or as a
 * cross-region fallback when the viewer's local region returns 404.
 * The frontend fetches the data here, then posts it to their local backend to
 * create the copy (see saveSharedChatCopy).
 * @domain REVIEW
 */
export const getSharedChatFromRegion = (
  shareToken: string,
  region: Region
): Promise<{ data: SharedChatResponse }> => {
  const baseUrl = getUrlFromConfig(region);
  const token = getAuthTokenForHttpClientSync();
  const tempUserId = getTempUserId();
  return axios.get<SharedChatResponse>(
    `${baseUrl}/api/v2/ml-scribe/chat/shared/${shareToken}`,
    {
      params: { create_copy: false },
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(tempUserId ? { 'X-Temp-User-ID': tempUserId } : {}),
      },
    }
  );
};

/**
 * Create a local copy of a shared chat in the viewer's local region backend.
 * Called after getSharedChatFromAU succeeds, to store the chat locally.
 * @domain REVIEW
 */
export const saveSharedChatCopy = (shareToken: string, body: SharedChatCopyRequest) => {
  return scribeHttpV2.post<SharedChatResponse>(`/chat/shared/${shareToken}/copy`, body);
};

// Carved to `@heidi/templates/data` at ARCFE-1019 with a recorded DOMAIN
// RULING (was `@domain REVIEW`): `/mk-template-requests/` is a template
// request — templates by RESOURCE, no competing claimant.
export { createMKTemplateRequest } from '@heidi/templates/data';

/** @domain REVIEW */
export const getMyMKTemplateRequests = (params?: {
  page?: number;
  page_size?: number;
}) => {
  return scribeHttpV2.get<MKPaginatedTemplateRequestResponse>(
    '/mk-template-requests/my-requests',
    {
      params: {
        page: params?.page,
        page_size: params?.page_size,
      },
    }
  );
};

/** @domain REVIEW */
export const getMKTemplateRequestById = (requestId: string) => {
  return scribeHttpV2.get<MKCustomTemplateRequestResponse>(
    `/mk-template-requests/${requestId}`
  );
};

/**
 * Send Get Mobile Email
 * @description Send an email to the user with a link to get Heidi for mobile
 * @domain workspace
 */
export const sendGetMobileEmail = (input: GetMobileRequest) => {
  return scribeHttpV2.post<void>('/user/get-mobile', input);
};

export type PrechartingToggleResponse = {
  is_precharting_enabled: boolean;
};

export type PrechartQueueResponse = {
  queued_count: number;
};

export type PrechartStatsBucket = {
  hour: string;
  clinic_id: string;
  kinde_user_id: string;
  counts: Record<string, number>;
};

export type PrechartStatsResponse = {
  buckets: PrechartStatsBucket[];
};

/** @domain REVIEW */
export const getPrechartingEnabled = () => {
  return scribeHttpV2.get<PrechartingToggleResponse>('/prechart/is-precharting-enabled');
};

/** @domain REVIEW */
export const togglePrechartingEnabled = () => {
  return scribeHttpV2.post<PrechartingToggleResponse>(
    '/prechart/toggle-is-precharting-enabled'
  );
};

/** @domain REVIEW */
export const queuePrechartsForUser = () => {
  return scribeHttpV2.post<PrechartQueueResponse>('/prechart/trigger-enqueue-by-user');
};

/** @domain REVIEW */
export const getPrechartStats = () => {
  return scribeHttpV2.get<PrechartStatsResponse>('/prechart/admin/stats');
};

export type PrechartAdminEnableRequest = {
  email: string;
};

export type PrechartAdminEnableResponse = {
  is_precharting_enabled: boolean;
  email: string;
};

/** @domain REVIEW */
export const enablePrechartForUser = (input: PrechartAdminEnableRequest) => {
  return scribeHttpV2.post<PrechartAdminEnableResponse>('/prechart/admin/enable', input);
};

// Carved to `@heidi/patients/data` with `useSubmitPrechartFeedback`. Same rule
// as the instructions pair below: the endpoint follows its hook, and the hub
// keeps name-intact re-exports for existing importers and `jest.mock` literals.
export type {
  PrechartFeedbackRating,
  PrechartFeedbackRequest,
} from '@heidi/patients/contracts';
export { submitPrechartFeedback } from '@heidi/patients/data';

// Carved to `@heidi/patients/data` with the prechart-instructions hook pair;
// name-intact re-exports keep importers and `jest.mock` literals resolving. The
// rest of `/prechart/**` keeps its bodies and its tag — its callers have not carved.
export type { PrechartInstructionsResponse } from '@heidi/patients/contracts';
export {
  getPrechartInstructions,
  updatePrechartInstructions,
} from '@heidi/patients/data';

export type GeneratePrechartDocumentResponse = {
  document_id: string;
  content: string;
  template_id: string | null;
};

/** @domain REVIEW */
export const generatePrechartDocument = (
  sessionId: string,
  templateId?: string | null
) => {
  return scribeHttpV2.post<GeneratePrechartDocumentResponse>(
    `/prechart/${sessionId}/generate-prechart-document`,
    templateId ? { template_id: templateId } : {}
  );
};
// ========== Protocol API ==========

import type {
  ConnectorListResponse,
  CreateProtocolRequest,
  GenerateProtocolRequest,
  GenerateProtocolResponse,
  Protocol,
  ProtocolListResponse,
  UpdateProtocolRequest,
} from '@/types/protocol';

export type { Protocol, ProtocolListResponse };

/**
 * Get all protocols (system + user)
 * @domain REVIEW
 */
export const getProtocols = () => {
  return scribeHttpV2.get<ProtocolListResponse>('/protocols');
};

/**
 * Get a single protocol by ID
 * @domain REVIEW
 */
export const getProtocolById = (protocolId: string) => {
  return scribeHttpV2.get<Protocol>(`/protocols/${protocolId}`);
};

/**
 * Get a protocol by shorthand
 * @domain REVIEW
 */
export const getProtocolByShorthand = (shorthand: string) => {
  return scribeHttpV2.get<Protocol>(`/protocols/by-shorthand/${shorthand}`);
};

/**
 * Create a new protocol
 * @domain REVIEW
 */
export const createProtocol = (input: CreateProtocolRequest) => {
  return scribeHttpV2.post<Protocol>('/protocols', input);
};

/**
 * Update a protocol
 * @domain REVIEW
 */
export const updateProtocol = (protocolId: string, input: UpdateProtocolRequest) => {
  return scribeHttpV2.patch<Protocol>(`/protocols/${protocolId}`, input);
};

/**
 * Delete a protocol
 * @domain REVIEW
 */
export const deleteProtocol = (protocolId: string) => {
  return scribeHttpV2.delete<{ success: boolean }>(`/protocols/${protocolId}`);
};

/**
 * Upload a reference document to a protocol
 * @domain REVIEW
 */
export const uploadProtocolDocument = (protocolId: string, file: File) => {
  const formData = new FormData();
  formData.append('file', file);
  return scribeHttpV2.post<Protocol>(`/protocols/${protocolId}/documents`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

/**
 * Delete a reference document from a protocol
 * @domain REVIEW
 */
export const deleteProtocolDocument = (protocolId: string, documentKey: string) => {
  return scribeHttpV2.delete<Protocol>(
    `/protocols/${protocolId}/documents/${encodeURIComponent(documentKey)}`
  );
};

/**
 * Generate protocol fields using AI
 * @domain REVIEW
 */
export const generateProtocol = (input: GenerateProtocolRequest) => {
  return scribeHttpV2.post<GenerateProtocolResponse>('/protocols/generate', input);
};

/**
 * Get available connectors
 * @domain REVIEW
 */
export const getAvailableConnectors = () => {
  return scribeHttpV2.get<ConnectorListResponse>('/protocols/connectors');
};

// ========== Capabilities API ==========
// The three read fns carved to `@heidi/workspace/data` (IP2, ARCFE-956;
// REVIEW → workspace carve-time ruling). Name-intact re-exports; the
// mutation-side `updateCapabilityActions` stays (mutation dim is a later
// slice) reading its types through `@/types/capability`'s split shim.

import type { UpdateActionsRequest, UserCapabilityConfig } from '@/types/capability';

export type {
  CapabilityListResponse,
  CapabilityWithUserConfigListResponse,
} from '@heidi/workspace/contracts';
export type { UserCapabilityConfig };

export {
  getCapabilities,
  getCapabilityAutocomplete,
  getUserCapabilities,
} from '@heidi/workspace/data';

/**
 * Update enabled actions for a capability
 * @domain REVIEW
 */
export const updateCapabilityActions = (slug: string, input: UpdateActionsRequest) => {
  return scribeHttpV2.patch<UserCapabilityConfig>(`/capabilities/${slug}/actions`, input);
};

// ============================================================================
// Entity Verification API
// ============================================================================

import type {
  AnalyzeNoteResponse,
  ClinicalNotesAnalysis,
  GetEntityFeedbackListResponse,
  GetOverallFeedbackResponse,
  StartAutoVerificationResponse,
  SubmitEntityFeedbackRequest,
  SubmitEntityFeedbackResponse,
  SubmitOverallFeedbackRequest,
  SubmitOverallFeedbackResponse,
} from '@/types/entity-verification';

/**
 * Trigger entity verification analysis for a session's clinical note
 * @description Runs two-step LLM analysis: entity extraction and verification
 * @domain session
 */
export const analyzeSessionNote = (sessionId: string) => {
  return scribeHttpV2.post<AnalyzeNoteResponse>(
    `/sessions/${sessionId}/entity-verification/analyze`
  );
};

/**
 * Get the most recent entity verification analysis for a session
 * @description Returns the analysis if it exists, or null if not. While an
 * auto-mode pipeline is in-flight, the returned document's `status` will be
 * non-terminal (e.g. `extracting`, `verifying`) and its
 * `entity_verification_data` may be empty until the pipeline completes.
 * @domain session
 */
export const getSessionAnalysis = (sessionId: string) => {
  return scribeHttpV2.get<ClinicalNotesAnalysis | null>(
    `/sessions/${sessionId}/entity-verification/`
  );
};

/**
 * Kick off an auto-mode entity verification pipeline for a session.
 * @description Spawns a background task on the BE that waits for the note to
 * finish generating, then runs extraction + verification. Idempotent: if an
 * in-flight analysis already exists for the session's latest note, returns a
 * pointer to that one. The caller should poll `getSessionAnalysis` until
 * `status` becomes terminal (`completed` or `failed`).
 * @domain session
 */
export const startAutoVerification = (sessionId: string, config?: AxiosRequestConfig) => {
  return scribeHttpV2.post<StartAutoVerificationResponse>(
    `/sessions/${sessionId}/entity-verification/auto-start`,
    undefined,
    config
  );
};

/**
 * Submit or update entity verification feedback (upsert)
 * @description Creates or updates feedback for a specific entity in an analysis
 * @domain session
 */
export const submitEntityFeedback = (
  sessionId: string,
  request: SubmitEntityFeedbackRequest
) => {
  return scribeHttpV2.put<SubmitEntityFeedbackResponse>(
    `/sessions/${sessionId}/entity-verification/feedback`,
    request
  );
};

/**
 * Get all entity verification feedback for a session
 * @description Returns feedback records to hydrate the UI on load
 * @domain session
 */
export const getEntityFeedback = (sessionId: string) => {
  return scribeHttpV2.get<GetEntityFeedbackListResponse>(
    `/sessions/${sessionId}/entity-verification/feedback`
  );
};

/**
 * Submit overall verification feedback for a session
 * @description Creates or updates session-level feedback for the verification run
 * @domain session
 */
export const submitOverallFeedback = (
  sessionId: string,
  request: SubmitOverallFeedbackRequest
) => {
  return scribeHttpV2.put<SubmitOverallFeedbackResponse>(
    `/sessions/${sessionId}/entity-verification/overall-feedback`,
    request
  );
};

/**
 * Get overall verification feedback for a session
 * @description Returns overall feedback if previously submitted
 * @domain session
 */
export const getOverallFeedback = (sessionId: string) => {
  return scribeHttpV2.get<GetOverallFeedbackResponse>(
    `/sessions/${sessionId}/entity-verification/overall-feedback`
  );
};

// --- Link Share (sender-side, authenticated) ---

/** @domain session */
export const getSessionLinkShares = (sessionId: string) => {
  return scribeHttpV2.get<GetSessionLinkSharesResponse>(
    `sessions/${sessionId}/link-shares`
  );
};

/** @domain session */
export const createSessionLinkShare = (
  sessionId: string,
  request: CreateSessionLinkShareRequest
) => {
  return scribeHttpV2.post<CreateSessionLinkShareResponse>(
    `sessions/${sessionId}/link-shares`,
    request
  );
};

/** @domain session */
export const revokeSessionLinkShareRecipient = (
  sessionId: string,
  shareToken: string
) => {
  const token = encodeURIComponent(shareToken);
  return scribeHttpV2.delete<RevokeSessionLinkShareResponse>(
    `sessions/${sessionId}/link-shares/recipients/${token}`
  );
};

// --- Layered settings (GET/PATCH /api/v2/ml-scribe/settings) ---

export type ResolvedSettingView = components['schemas']['ResolvedSettingResponse'];

export type SettingsCollectionResponse =
  components['schemas']['SettingsCollectionResponse'];

/** @domain REVIEW */
export const getResolvedSettings = (keys: string[]) => {
  return scribeHttpV2.get<SettingsCollectionResponse>('settings', {
    params: { keys: keys.join(',') },
  });
};

/**
 * Three-state write body keyed by setting key: a value sets it, explicit null
 * clears it (resolution falls back to a lower layer or the catalog default),
 * an absent key is unchanged.
 * @domain REVIEW
 */
export const patchSettings = (values: Record<string, unknown>) => {
  return scribeHttpV2.patch<SettingsCollectionResponse>('settings', values);
};

/** @domain workspace */
// Moved to `@heidi/workspace/data` (Wave 2 · W, ARCFE-801).
export { getEntitlements } from '@heidi/workspace/data';
// TRANSFERRED workspace → coding at the SCC inversion (ARCFE-986): the mint
// was the disguised cross-domain command; the name stays hub-intact.
export { mintCodingCaseAccessContext } from '@heidi/coding/data';

// ---------------------------------------------------------------------------
// Inbox / Conversations
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Patients (calls)
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Reminder Configurations
// ---------------------------------------------------------------------------

// Carved to `@heidi/calls/data` with the `calls/reminders` family (D1,
// ARCFE-1045) — the first calls family to move. The `@domain calls` tags
// retire with the carve; re-exported name-intact so existing importers and
// their `jest.mock` literals keep resolving until Z1's codemod.
//
// Transport is unchanged: the package fns resolve `scribeHttpV2` through the
// `@heidi/api/client` registry, which app wiring sets to this module's own
// singleton. Note for test authors: a suite that mocks `@/utils/scribeHTTP`
// no longer intercepts these seven — it must ALSO set a registry double
// (`setScribeHttpClients`). See `src/__tests__/api/scribeAPIV2.calls.test.ts`.
export {
  createReminderConfig,
  deleteReminderConfig,
  getReminderConfigs,
  processReminders,
  retryAppointmentReminder,
  toggleReminderConfig,
  updateReminderConfig,
} from '@heidi/calls/data';

// Carved to `@heidi/session/data` at the session opener (ARCFE-983); the
// REVIEW tag was ruled `@domain session` at carve (sole caller is the
// feedback family's hook). Name-intact re-exports.
export type {
  RecordNoteSuggestionFeedbackRequest,
  RecordNoteSuggestionFeedbackResponse,
} from '@heidi/session/contracts';
export { recordNoteSuggestionFeedback } from '@heidi/session/data';

export type GraduateNoteSuggestionResponse = {
  suggestion_id: string;
  applied: boolean;
};

/**
 * Graduate an `opt_in_ask` suggestion (adaptive-learning v3). Server-side this
 * applies the previewed edit to the current note AND sets the adaptive-learning
 * opt-in, then returns 200. Called on "Turn on".
 *
 * @domain REVIEW
 */
export const graduateNoteSuggestion = (input: { suggestionId: string }) => {
  return scribeHttpV2.post<GraduateNoteSuggestionResponse>(
    `/note-suggestions/${encodeURIComponent(input.suggestionId)}/graduate`
  );
};

export type AdaptiveLearningAvailabilityResponse = {
  available: boolean;
};

/**
 * Whether the adaptive-learning settings card should show for this user
 * (master flag `release-scribe-adaptive-learning` ON AND the user has learned
 * rules). Isolated on its own path so the endpoint is easy to adjust while the
 * backend half lands in parallel; the query hook treats a 404 as not-available.
 *
 * @domain REVIEW
 */
export const getAdaptiveLearningAvailability = () => {
  return scribeHttpV2.get<AdaptiveLearningAvailabilityResponse>(
    `/note-suggestions/adaptive-learning/availability`
  );
};

// === API Status Check Log ===

export type ApiStatusCheckResultPayload = {
  url: string;
  ok: boolean;
  status?: number;
  error?: string;
};

export type LogApiStatusCheckRequest = {
  results: ApiStatusCheckResultPayload[];
};

export type LogApiStatusCheckResponse = {
  success: boolean;
  log_id: string;
};

/** @domain REVIEW */
export const logApiStatusCheck = (
  body: LogApiStatusCheckRequest
): Promise<AxiosResponse<LogApiStatusCheckResponse>> => {
  return scribeHttpV2.post<LogApiStatusCheckResponse>('/api-status-check-log', body);
};

// === Client SDK Diagnostics ===

export type LogClientSdkDiagnosticsRequest = {
  report_version: 1;
  webview_origin: string;
  page_path: string;
  flagsmith: {
    provider_status: string;
    resolved: boolean;
  };
  datadog: {
    configured: boolean;
    rum_initialized: boolean;
    logs_initialized: boolean;
    rum_action_queued: boolean;
    transport_probe: 'reachable' | 'failed' | 'not_attempted';
  };
  build: {
    environment: string;
    release: string;
    platform: 'desktop' | 'web';
    installer_type: string;
    build_id?: string;
  };
};

export type LogClientSdkDiagnosticsResponse = {
  success: boolean;
};

/**
 * NOT INTEGRATIONS — retagged OUT of the `ehr` block rather than with it
 * (ARCFE-1064, 2026-08-14). When `@domain ehr` unified into
 * `@domain integrations`, 55 of the 57 endpoints went across because their
 * routes are genuinely integrations resources (`/ehr/**`, `/ehr-launch/**`,
 * `/integrations/**`). These two do not: the route is `/diagnostics`, a
 * platform observability channel — the backend relays the payload to Datadog
 * for webviews that cannot reach it directly (see the block above
 * `LogFrontendDiagnosticsRequest`) — and the second caller,
 * `useLinkContextLoadingDiagnostics.ts`, is not integrations code at all. The
 * integrations lane measured this and said so at ARCFE-1015: "2 of the 56
 * route OUT". Carrying them across would have laundered a mis-tag into a
 * PACKAGED domain and put them behind the hub admission gate under an owner
 * that does not want them. OWNER QUESTION for the REVIEW burn-down: who owns
 * the frontend -> backend observability channel? It is not a product domain
 * on current evidence.
 *
 * @domain REVIEW
 */
export const logClientSdkDiagnostics = (
  body: LogClientSdkDiagnosticsRequest
): Promise<AxiosResponse<LogClientSdkDiagnosticsResponse>> => {
  return scribeHttpV2.post<LogClientSdkDiagnosticsResponse>('/diagnostics', body);
};

// === Frontend Diagnostics Log ===

/**
 * Generic frontend → backend diagnostics channel. Some webviews (e.g. Epic
 * Haiku behind a hospital firewall) cannot reach Datadog directly, so the
 * frontend can't self-report via the browser logger. This endpoint rides the
 * normal authenticated API path (which is reachable) and lets the backend log
 * the payload to Datadog server-side, keyed on `event` + `session_id`.
 *
 * `data` is an open bag of JSON-serialisable diagnostic fields — keep it small
 * and free of PHI.
 */
export type LogFrontendDiagnosticsRequest = {
  event: string;
  session_id?: string | null;
  data: Record<string, unknown>;
};

export type LogFrontendDiagnosticsResponse = {
  success: boolean;
};

/**
 * NOT INTEGRATIONS, for the reason recorded on `logClientSdkDiagnostics`
 * above (ARCFE-1064): `/diagnostics/events` is the same platform
 * observability channel, and the JSDoc on its request type says so in its own
 * words. Stays REVIEW until the observability owner question is answered.
 *
 * @domain REVIEW
 */
export const logFrontendDiagnostics = (
  body: LogFrontendDiagnosticsRequest
): Promise<AxiosResponse<LogFrontendDiagnosticsResponse>> => {
  return scribeHttpV2.post<LogFrontendDiagnosticsResponse>('/diagnostics/events', body);
};
